<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
// import { InfoFilled } from '@element-plus/icons-vue' // désactivé avec le tooltip "i" descriptif (cf. plus bas)
import { useCompteurStore } from '@/store/compteur.store'
import { useParameterStore } from '@/store/parameter.store'
import { useCenterParameterStore } from '@/store/centerParameter.store'
import { useGroupTemplateStore } from '@/store/groupTemplate.store'
import { useUsersStore } from '@/store/users.store'
import { addBoarBesoinPart, addFreeDispo, bestFitConcentrationKey, COLUMN_OPENING_NATURES, compareGroupKeys, consumeFreeDispoSources, allocateGenericBesoinSurplus, coverBoarBesoinsInGroupOrder, dispoCoversGenericBesoin, emptyFreeDispo, excessGroupKey, freeDispoLeftoverByGroup, loosePoolKeyForDispo, type FreeDispoBucket } from '@/composables/CompteurGroupingComposable'
import { useOptions } from '@/store/options.store'
import { FAVORITE_BUILDING_OPTION, VOLUME_STANDARD_OPTION } from '@/constantes/options-constantes'
import { useParcCubicles } from '@/composables/ParcCubiclesComposable'
import { parcGroupLabels, useParcPlanGroups } from '@/composables/ParcPlanGroupsComposable'
import { useBuildingStore } from '@/store/building.store'
import { ConditioningMethods } from '@/constantes/constants'
import { PARAM_FAMILLE_PRODUCTION, PARAM_CRITERE_CONDITIONNEMENT } from '@/constantes/parameters-constantes'
import { addDaysToLocalISODate, getDateLongFrenchFormat, todayLocalISODate } from '@/helper/dateFormatter'
import type { IParameter } from '@/models/parameter'
import type { ICompteur, CompteurSegment } from '@/models/compteur'
import { useBoars } from '@/store/boars.store'
import { useDevOptionsStore } from '@/store/devOptions.store'
import AnimalService from '@/services/AnimalService'
import ReservedBoarsBadge from '@/components/Compteurs/ReservedBoarsBadge.vue'
import PlanDeMonteBadge from '@/components/Compteurs/PlanDeMonteBadge.vue'
import LoadingSteps from '@/components/Loading/LoadingSteps.vue'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Colonne dynamique : triplet schéma × volume × concentration. */
interface ColumnDef {
  key: string
  label: string        // "HMX", "HMX 40 [27]", "HMX [27]"
  schemaId: number
  schemaCode: string
  volume: number | null
  concentration: number | null
  /** Schéma hors préférences configurées du centre (ex. THO non sélectionné) : la colonne
   * doit quand même apparaître — une activité réelle ne doit jamais être masquée — mais
   * repoussée en fin de tableau, simplifiée (pas de détail volume/concentration, juste le
   * code schéma) et mise en évidence côté en-tête (cf. `columns` et `col-unconfigured*`). */
  isUnconfigured: boolean
  /** Clé(s) `byCol` réellement agrégées par cette colonne. Pour une colonne configurée,
   * c'est `[key]` (1:1). Pour un schéma hors config, une seule colonne "simple" regroupe
   * toutes les variantes volume/concentration trouvées dans les données — `matchKeys`
   * porte alors chacune des clés `${schemaId}__${vol}__${conc}` sous-jacentes à sommer. */
  matchKeys: string[]
}

/** Valeur agrégée pour une cellule grise. */
interface CellVal {
  besoin: number
  dispo: number
}

// `bestFitConcentrationKey` vit désormais dans CompteurGroupingComposable, avec les autres règles
// de rapprochement — c'est là qu'elle est testable (Mantis 0028524).

/**
 * Rattachement d'une ligne à son "encart" (bloc coloré) repliable.
 * `encartId` regroupe les lignes d'un même encart (ex. un conditionnement Mono,
 * le bloc Hétéro Standard...) ; `isEncartHeader` marque la ligne bandeau/en-tête
 * sur laquelle on clique pour replier/déplier les autres lignes du même encart.
 */
interface EncartMeta {
  encartId: string
  isEncartHeader: boolean
  /** Identifiant stable de la ligne (posé par `tableData`) — requis par `el-table` `row-key`
   * pour ne pas désynchroniser la colonne figée quand le nombre de lignes change (repli/dépli). */
  rowKey?: string
}

/** Ligne du bloc Attente (dispo non orienté, U07) — nature=DispoAttente, cf. E30-F01-U07-Attente. */
interface AttenteRow extends EncartMeta {
  _type: 'attente'
  label: string
  byCol: Map<string, number>
  /** Prédicat identifiant, parmi `compteurStore.datas`, les lignes brutes qui composent cette
   * ligne (détail par verrat au clic sur une cellule) — cf. `cellDetail`. */
  detailPredicate?: (c: ICompteur) => boolean
  /** `byCol` porte un COUNT(DISTINCT boarId) PAR COLONNE (store), pas une somme : le total ne
   * doit jamais sommer ces comptes par colonne (un même verrat présent sur 2 colonnes serait
   * compté 2 fois) — il faut recompter les verrats distincts sur l'ensemble des lignes brutes
   * matchées (cf. getVal/getTot, décision 2026-07-28). */
  isDistinctCount?: boolean
}

/** En-tête d'un bloc conditionnement (fond gris, gras). */
interface CondRow extends EncartMeta {
  _type: 'cond'
  label: string        // "Selfix"
  byCol: Map<string, CellVal>
}

/** Ligne G1 / G1 réservé / G2 / G2 réservé... */
interface GroupRow extends EncartMeta {
  _type: 'group'
  label: string        // "G1 réservé" ou "G1"
  isReserved: boolean
  byCol: Map<string, CellVal>
}

type HeteroVariant = 'standard' | 'armada'

/** Bandeau de section hétéro (magenta = Standard, bleu = Armada). */
interface HeteroSectionRow extends EncartMeta {
  _type: 'heteroSection'
  variant: HeteroVariant
  label: string        // "Hétéro Standard" / "Hétéro Armada"
  byCol: Map<string, CellVal>
}

/** Ligne compteur sous le bandeau ("Nbre de doses orientées" / "Nb verrats orientés"). */
interface CounterRow extends EncartMeta {
  _type: 'counter'
  variant: HeteroVariant
  label: string
  byCol: Map<string, number>
  detailPredicate?: (c: ICompteur) => boolean
  /** cf. AttenteRow.isDistinctCount. */
  isDistinctCount?: boolean
}

/** En-tête conditionnement au sein d'un bloc hétéro (HS-Selfix, AB-Blister...). Le Besoin
 * hétéro existe (segment=HeteroStandard/HeteroArmada, cf. discussion "AB - Blister") : valeur
 * = diff dispo-besoin, comme le bloc gris, pas une somme brute. */
interface HeteroCondRow extends EncartMeta {
  _type: 'heteroCond'
  variant: HeteroVariant
  label: string
  byCol: Map<string, CellVal>
}

/** Ligne G1 / G2 / HG d'un conditionnement hétéro — pas de distinction réservé/standard
 * (contrairement au bloc gris) : une seule ligne par groupe, cf. décision produit. */
interface HeteroGroupRow extends EncartMeta {
  _type: 'heteroGroup'
  variant: HeteroVariant
  label: string
  byCol: Map<string, CellVal>
}

/** Bandeau de section "Parc" (vert). */
interface ParcSectionRow extends EncartMeta {
  _type: 'parcSection'
  label: string
  byCol: Map<string, number>
  /** Jamais utilisés (pas de détail sur le bandeau) — présents uniquement pour que l'union
   * NUMBER_MAP_TYPES partage les mêmes champs optionnels que AttenteRow/CounterRow/ParcRow. */
  detailPredicate?: (c: ICompteur) => boolean
  isDistinctCount?: boolean
}

/** Ligne du bloc Parc (Nb Verrats PDM, G1/G2/G3, Nb Prélèvements...). */
interface ParcRow extends EncartMeta {
  _type: 'parcRow'
  label: string
  bold: boolean
  indent: boolean
  byCol: Map<string, number>
  detailPredicate?: (c: ICompteur) => boolean
  /** cf. AttenteRow.isDistinctCount. */
  isDistinctCount?: boolean
}

/** Bandeau de section "Stock" (orange). */
interface StockSectionRow extends EncartMeta {
  _type: 'stockSection'
  label: string
  byCol: Map<string, number>
  detailPredicate?: (c: ICompteur) => boolean
  /** Jamais utilisé (Stock = une somme de doses, pas un compte de verrats) — cf. ParcSectionRow. */
  isDistinctCount?: boolean
}

/** Ligne conditionnement du bloc Stock (Selfix, HS-Selfix, AS-Selfix...). */
interface StockRow extends EncartMeta {
  _type: 'stockRow'
  label: string
  byCol: Map<string, number>
  detailPredicate?: (c: ICompteur) => boolean
  /** Jamais utilisé (Stock = une somme de doses, pas un compte de verrats) — cf. ParcSectionRow. */
  isDistinctCount?: boolean
}

type AnyRow = AttenteRow | CondRow | GroupRow | HeteroSectionRow | CounterRow | HeteroCondRow | HeteroGroupRow | ParcSectionRow | ParcRow | StockSectionRow | StockRow

// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------

const { t } = useI18n()
const compteurStore       = useCompteurStore()
const parameterStore      = useParameterStore()
const centerParameterStore = useCenterParameterStore()
const groupTemplateStore  = useGroupTemplateStore()
const usersStore          = useUsersStore()
const boarsStore          = useBoars()
const buildingStore       = useBuildingStore()
const optionsStore        = useOptions()
const { datas, isLoading, loadingMessage, version, loadVersion, razInProgress, razLoadingSteps } = storeToRefs(compteurStore)

/**
 * Volume auquel se sert une commande qui n'exige aucun volume (cf. dispoCoversGenericBesoin).
 * NaN si l'option du centre est absente ou non numérique — le getter le signale en console et le
 * rapprochement retombe alors sur son comportement sans condition de volume.
 */
const defaultBesoinVolume = computed(() =>
  optionsStore.getNumericOptionByCode(VOLUME_STANDARD_OPTION, usersStore.currentUser, usersStore.currentCenter),
)

// ---------------------------------------------------------------------------
// Repli des encarts (blocs colorés) : clic sur la ligne bandeau/en-tête d'un
// encart pour replier/déplier ses lignes de détail. État en mémoire (non
// persisté), clé = row.encartId.
//
// Les lignes repliées sont retirées de `tableData` (disparition nette, pas de
// ligne fantôme qui reste affichée en réduit). Seul le repli est animé : un
// court fondu (`fadingEncarts` -> classe CSS `row-fading`) avant la suppression
// réelle des lignes, le temps que l'opacité descende à 0. Le dépli reste
// instantané (une ligne qui n'existe pas encore ne peut pas apparaître "en fondu"
// sans complexité disproportionnée avec un <table> natif).
// ---------------------------------------------------------------------------

const collapsedEncarts = reactive<Record<string, boolean>>({})
const fadingEncarts = reactive<Record<string, boolean>>({})
const ENCART_FADE_MS = 150

function toggleEncart(encartId: string): void {
  if (collapsedEncarts[encartId]) {
    collapsedEncarts[encartId] = false
    return
  }
  fadingEncarts[encartId] = true
  setTimeout(() => {
    collapsedEncarts[encartId] = true
    fadingEncarts[encartId] = false
  }, ENCART_FADE_MS)
}

// ---------------------------------------------------------------------------
// Navigation date (helpers purs dans src/helper/dateFormatter.ts)
// ---------------------------------------------------------------------------

const currentDate = ref(todayLocalISODate())

function prevDay(): void {
  currentDate.value = addDaysToLocalISODate(currentDate.value, -1)
}

function nextDay(): void {
  currentDate.value = addDaysToLocalISODate(currentDate.value, 1)
}

const isToday = computed(() => currentDate.value === todayLocalISODate())

watch(currentDate, (date) => compteurStore.load({ date }), { immediate: true })

// REQ904 : mode anticipation — ignore Dispo/Stock dans le rapprochement (ne montre que les
// commandes et le plan de monte, pour anticiper la production à venir).
const anticipationMode = ref(false)

/** Le toggle de l'écran est libellé « Prise en compte du stock » : il expose donc l'INVERSE du
 * mode anticipation, qui lui IGNORE le stock. Le switch était branché directement sur
 * `anticipationMode`, si bien que l'activer vidait les blocs Blister/Selfix/Vrac au lieu de les
 * remplir (Mantis 0028293). Position par défaut : activé, le stock est bien pris en compte. */
const stockTakenIntoAccount = computed<boolean>({
  get: () => !anticipationMode.value,
  set: (taken) => { anticipationMode.value = !taken },
})

// Masque les valeurs à 0 dans le tableau (décrochage visuel) — sauf les lignes "Gx réservé"
// (row._type === 'group' && isReserved), qui doivent toujours afficher 0 même sans commande réservée
// couvrant tout le besoin, pour signaler explicitement qu'un statut réservé existe sur ce créneau.
const showZeroValues = ref(false)

// U02 : préférences de schémas (sélection + ordre) — indépendant de la date, chargé une fois.
onMounted(() => {
  compteurStore.loadSchemaPrefs()

  // Conditionnements configurés du centre (PRODUCTION/CONDITIONNEMENT) : nécessaires pour
  // afficher la liste fixe des lignes Mono même sans donnée du jour (cf. greyRows).
  const centerId = usersStore.currentCenter?.id
  if (centerId != null && centerParameterStore.loadedCenterId !== centerId) {
    centerParameterStore.load({ page: 0, pageSize: 0, include: '*', centerId })
  }

  // Gabarits de groupe (couleur + plage horaire par rang, paramétrage "Groupe de départ") :
  // pilotent le regroupement G1/G2/G3 des Besoins dans le bloc gris Mono (cf. greyRows).
  groupTemplateStore.load(centerId)
})

// ---------------------------------------------------------------------------
// Colonnes dynamiques (axe X) : triplet schéma × volume × concentration
// ---------------------------------------------------------------------------

/** Arrondi à 2 décimales pour l'affichage (ex. 24.210526 -> 24.21) sans zéros inutiles pour
 * une valeur entière (27 reste "27", pas "27.00") — la valeur brute (non arrondie) reste
 * utilisée pour le calcul des clés de colonne (dataKey), seul l'affichage change. */
function formatConcentration(n: number): number {
  return Math.round(n * 100) / 100
}

// U02 : position de chaque schéma dans les préférences (sélection + ordre du centre),
// pour trier les colonnes selon ce choix plutôt qu'alphabétiquement.
const schemaPrefOrderIndex = computed(() => {
  const m = new Map<number, number>()
  compteurStore.orderedVisibleSchemaIds.forEach((id, index) => m.set(id, index))
  return m
})

const columns = computed<ColumnDef[]>(() => {
  const colMap = new Map<string, ColumnDef>()

  // U02 : les préférences de schémas du centre (sélection + ordre) pilotent les colonnes.
  // Si aucune préférence n'est encore configurée, pas de filtre : on retombe sur les
  // schémas présents dans les données du jour.
  const prefsActive = compteurStore.orderedVisibleSchemaIds.length > 0
  const visibleSchemaIds = prefsActive ? new Set(compteurStore.orderedVisibleSchemaIds) : null

  // REQ887 : un schéma NON sélectionné n'ouvre une colonne que s'il porte du PRÉVISIONNEL —
  // une commande (Besoin), du stock (Stock), des doses au plan de monte (Baseline) — OU un
  // PRÉLÈVEMENT constaté pas encore orienté (ParcPreleve, DispoAttente) : cf. COLUMN_OPENING_
  // NATURES (CompteurGroupingComposable). Les doses issues d'un plan de monte sélectionné doivent
  // rester visibles (arbitrage métier 2026-08-17) : sans colonne, l'opérateur ne verrait pas
  // qu'un schéma est planifié. Un prélèvement sur un schéma non configuré ne doit pas non plus
  // disparaître silencieusement (bug rapporté 2026-08-31).
  // À l'inverse, `Dispo` (dispatch VALIDÉ/orienté) reste seul exclu : il rejoint une colonne déjà
  // légitime, sinon il n'est pas affiché (Mantis 0028247 — un unique verrat hétéro orienté ouvrait
  // la colonne VAL sans qu'aucune commande/stock/plan de monte ne le justifie).
  // Tant qu'aucune préférence n'est configurée, la règle ne s'applique pas : le tableau serait
  // vide sans recours, on garde l'affichage intégral en "hors config" (cf. isUnconfigured).
  const columnOpeningSchemaIds = new Set(
    datas.value
      .filter((c) => c.schemaId != null && COLUMN_OPENING_NATURES.has(c.nature))
      .map((c) => c.schemaId!),
  )
  const canOpenColumn = (schemaId: number): boolean =>
    !prefsActive || visibleSchemaIds!.has(schemaId) || columnOpeningSchemaIds.has(schemaId)

  const baseSchemas: IParameter[] = compteurStore.orderedVisibleSchemaIds
    .map((id) => parameterStore.getParameterById(id))
    .filter((s): s is IParameter => s != null)

  for (const schema of baseSchemas) {
    const key = `${schema.id}____`
    colMap.set(key, { key, label: schema.code3 ?? '', schemaId: schema.id, schemaCode: schema.code3 ?? '', volume: null, concentration: null, isUnconfigured: false, matchKeys: [key] })
  }

  // Seul un Besoin (commande réelle) doit faire apparaître une nouvelle colonne spécifique
  // (volume/concentration) — Dispo/Stock/DispoAttente ne créent jamais de colonne dédiée (ex.
  // un stock + dispatch en HMX 85 [1800] sans commande correspondante ne doit pas faire
  // apparaître "HMX 85 [1800]"). Traité en 2e passe une fois les colonnes Besoin connues.
  const nonBesoinRows: typeof datas.value = []

  for (const c of datas.value) {
    if (c.schemaId == null) continue
    if (c.nature !== 'Besoin') { nonBesoinRows.push(c); continue }
    const schema = parameterStore.getParameterById(c.schemaId)
    if (!schema) continue
    // Schéma hors préférences (ex. THO non sélectionné par le centre) : on ne filtre plus la
    // ligne — une activité réelle (Besoin/Dispo...) ne doit jamais disparaître silencieusement —
    // on marque juste la colonne pour la repousser en fin de tableau et la mettre en évidence.
    // Si AUCUNE préférence n'est configurée (visibleSchemaIds null), tout est hors config par
    // défaut : tant que le centre n'a rien paramétré, tout s'affiche en "hors config" plutôt
    // qu'en configuré par défaut — pleinement explicable pour l'utilisateur.
    const isUnconfigured = !visibleSchemaIds?.has(c.schemaId)

    const code = schema.code3 ?? ''
    const vol  = c.volume        ?? null
    const conc = c.concentration ?? null
    const dataKey = `${c.schemaId}__${vol ?? ''}__${conc ?? ''}`

    if (isUnconfigured) {
      // Schéma hors config : pas de détail volume/concentration, une seule colonne
      // "simple" (juste le code, ex. "THO") qui agrège toutes les variantes trouvées.
      const key = `${c.schemaId}____`
      let col = colMap.get(key)
      if (!col) {
        col = { key, label: code, schemaId: c.schemaId, schemaCode: code, volume: null, concentration: null, isUnconfigured: true, matchKeys: [] }
        colMap.set(key, col)
      }
      if (!col.matchKeys.includes(dataKey)) col.matchKeys.push(dataKey)
      continue
    }

    if (!colMap.has(dataKey)) {
      let label = code
      if (vol  != null) label += ` ${vol}`
      if (conc != null) label += ` [${formatConcentration(conc)}]`
      colMap.set(dataKey, { key: dataKey, label, schemaId: c.schemaId, schemaCode: code, volume: vol, concentration: conc, isUnconfigured: false, matchKeys: [dataKey] })
    }
  }

  // 2e passe (Dispo/Stock/DispoAttente) : rejoint la colonne Besoin la plus proche au même volume
  // dont la concentration exigée est <= celle réellement obtenue (cf. bestFitConcentrationKey,
  // décision 2026-07-27) ; sinon rejoint la colonne de base du schéma (jamais de nouvelle colonne
  // spécifique créée par autre chose qu'un Besoin).
  // Une colonne ouverte par une commande à volume imposé SANS concentration reste candidate
  // (Mantis 0028524) : sans cela, elle s'affichait vide à jamais et les doses partaient dans la
  // colonne générique du schéma.
  for (const c of nonBesoinRows) {
    const schema = parameterStore.getParameterById(c.schemaId!)
    if (!schema) continue
    const vol  = c.volume        ?? null
    const conc = c.concentration ?? null
    const dataKey = `${c.schemaId}__${vol ?? ''}__${conc ?? ''}`

    const besoinCandidates = [...colMap.values()]
      .filter((col) => col.schemaId === c.schemaId && col.volume != null)
      .map((col) => ({ key: col.key, volume: col.volume, concentration: col.concentration }))
    const matchedKey = bestFitConcentrationKey(besoinCandidates, vol, conc)
    if (matchedKey) {
      const col = colMap.get(matchedKey)!
      if (!col.matchKeys.includes(dataKey)) col.matchKeys.push(dataKey)
      continue
    }

    const baseKey = `${c.schemaId}____`
    let col = colMap.get(baseKey)
    if (!col) {
      // REQ887 : ni sélectionné, ni commande, ni stock, ni plan de monte -> pas de colonne.
      if (!canOpenColumn(c.schemaId!)) continue
      const code = schema.code3 ?? ''
      const isUnconfigured = !visibleSchemaIds?.has(c.schemaId!)
      col = { key: baseKey, label: code, schemaId: c.schemaId!, schemaCode: code, volume: null, concentration: null, isUnconfigured, matchKeys: [baseKey] }
      colMap.set(baseKey, col)
    }
    if (!col.matchKeys.includes(dataKey)) col.matchKeys.push(dataKey)
  }

  return [...colMap.values()].sort((a, b) => {
    // Colonnes hors config toujours après les configurées, quel que soit l'ordre choisi.
    if (a.isUnconfigured !== b.isUnconfigured) return a.isUnconfigured ? 1 : -1
    const sc = prefsActive && !a.isUnconfigured
      ? (schemaPrefOrderIndex.value.get(a.schemaId) ?? 0) - (schemaPrefOrderIndex.value.get(b.schemaId) ?? 0)
      : a.schemaCode.localeCompare(b.schemaCode)
    if (sc !== 0) return sc
    // colonne de base (sans spécifique) en premier
    const aSpec = (a.volume != null ? 2 : 0) + (a.concentration != null ? 1 : 0)
    const bSpec = (b.volume != null ? 2 : 0) + (b.concentration != null ? 1 : 0)
    if (aSpec !== bSpec) return aSpec - bSpec
    if ((a.volume ?? 0) !== (b.volume ?? 0)) return (a.volume ?? 0) - (b.volume ?? 0)
    return (a.concentration ?? 0) - (b.concentration ?? 0)
  })
})

// El-table (Element Plus) fige l'ordre/layout de ses colonnes en interne (colgroup, calcul de
// largeurs...) au montage et ne les recalcule pas quand seul l'ORDRE du v-for change (les
// instances de `<el-table-column>` existantes sont juste déplacées/mises à jour, jamais
// démontées) : une réorganisation des préférences de schémas ne bougeait donc jamais les
// colonnes déjà affichées côté Compteurs, même avec un `:key` par colonne. Seul un
// remontage complet d'`<el-table>` refait ce calcul correctement — d'où cette signature
// (ordre+composition des colonnes), posée en `:key` sur `<el-table>` dans le template pour
// forcer ce remontage dès que l'ordre ou la sélection de schémas change.
const columnsSignature = computed(() => columns.value.map((c) => c.key).join('|'))

/** Clés `byCol` réellement portées par une colonne affichée — support du TOTAL (cf. getTot).
 * Une clé absente d'ici correspond à une donnée dont la colonne n'existe pas à l'écran. */
const displayedMatchKeys = computed(() => new Set(columns.value.flatMap((c) => c.matchKeys)))

// ---------------------------------------------------------------------------
// Bloc Attente (habillage jaune) — 4 lignes fixes, sur DEUX sources distinctes :
//  - les 2 lignes mono lisent la nature DispoAttente (U07-B2) ;
//  - les 2 lignes hétéro lisent la nature Dispo, restreinte aux lots créés non
//    conditionnés (cf. attenteLotsHetero*) — DispoAttente n'est jamais alimentée
//    par le flux d'orientation hétéro réel.
// ---------------------------------------------------------------------------

// Bloc non repliable (données toujours en attente d'orientation, à garder visibles) :
// aucune ligne n'est marquée isEncartHeader -> pas de toggle affiché ni cliquable.
// Labels sur une seule ligne (colonne libellé élargie, cf. .col-label) : tient sur 1
// ligne y compris en gras, quitte à abréger "attente" -> "att.".
/** Détail (clic cellule) : lignes brutes DispoAttente/Mono — même filtre que attenteDosesMono. */
function isAttenteMono(c: ICompteur): boolean {
  return c.nature === 'DispoAttente' && (c.segment ?? 'Mono') === 'Mono'
}
/** Détail : lignes brutes Dispo/Hétéro non conditionnées — même filtre que orienteDosesHetero*. */
function isOrienteHetero(segment: CompteurSegment) {
  return (c: ICompteur) => c.nature === 'Dispo' && (c.segment ?? 'Mono') === segment && c.conditionnementId == null
}
/** Détail de la ligne « Nbre de doses orientées » : restreint aux doses PAS ENCORE regroupées
 * dans un lot créé — même filtre que orienteDosesHetero*AvantLot (cf. compteur.store.ts, décision
 * 2026-08-27 : une fois dans un lot, ces doses basculent visuellement dans « Dispo Hétéro (att.) »). */
function isOrienteHeteroAvantLot(segment: CompteurSegment) {
  return (c: ICompteur) => isOrienteHetero(segment)(c) && c.nbBoars == null
}
/** Détail des lignes jaunes hétéro : restreint aux LOTS créés — même filtre que
 * attenteLotsHetero* (cf. compteur.store.ts, Mantis 0028300). */
function isLotHeteroAttente(segment: CompteurSegment) {
  return (c: ICompteur) => isOrienteHetero(segment)(c) && c.nbBoars != null
}

const attenteRows = computed<AttenteRow[]>(() => [
  { _type: 'attente', label: t('compteur.rows.dispoMonoAttente'),       byCol: compteurStore.attenteDosesMono,      encartId: 'attente', isEncartHeader: false, detailPredicate: isAttenteMono },
  { _type: 'attente', label: t('compteur.rows.nbVerratsDiff'),          byCol: compteurStore.attenteVerratsMono,     encartId: 'attente', isEncartHeader: false, detailPredicate: isAttenteMono, isDistinctCount: true },
  // Constat 2026-07-21 : "Dispo Hétéro Std/Arm (att.)" se lit sur la nature Dispo (segment Hétéro,
  // sans conditionnement) et PAS sur DispoAttente — cette dernière vise les dilutions hétéro dès la
  // création, jamais alimentée par le flux d'orientation réel.
  // Affiné (Mantis 0028300) : ces deux lignes ne comptent que les LOTS hétéro créés et non encore
  // conditionnés, pas toute orientation vers l'hétérospermie. Une fabrication mono marquée "H" sans
  // lot était comptée ici alors qu'aucun lot n'existait. La règle est celle du modèle du tableau
  // (Documents/Lot9/Haut tableau compteurs.png) : « Cumul doses des Lots en attentes [Hétéro Std].
  // Lot créé mais non conditionné. » Cf. attenteLotsHetero* dans compteur.store.ts.
  // Les blocs Hétéro, eux, gardent le total « Nbre de doses orientées » (getters
  // orienteDosesHetero*) : c'est bien leur objet, cf. « cumul des doses orientées » du même modèle.
  { _type: 'attente', label: t('compteur.rows.dispoHeteroStdAttente'),  byCol: compteurStore.attenteLotsHeteroStd,  encartId: 'attente', isEncartHeader: false, detailPredicate: isLotHeteroAttente('HeteroStandard') },
  { _type: 'attente', label: t('compteur.rows.dispoHeteroArmAttente'),  byCol: compteurStore.attenteLotsHeteroArm,  encartId: 'attente', isEncartHeader: false, detailPredicate: isLotHeteroAttente('HeteroArmada') },
])

// ---------------------------------------------------------------------------
// Partie grise : conditionnements × groupes
// ---------------------------------------------------------------------------

function emptyCell(): CellVal { return { besoin: 0, dispo: 0 } }

// ---------------------------------------------------------------------------
// Gabarits de groupe ("Groupe de départ", Options > Production) : pilotent le
// regroupement G1/G2/G3 des Besoins dans le bloc gris Mono ci-dessous.
// ---------------------------------------------------------------------------

/** Gabarits du centre, triés par rang (G1, G2, G3...). */
const sortedGroupTemplates = computed(() => [...groupTemplateStore.datas].sort((a, b) => a.rank - b.rank))

/** "HH:mm" ou "HH:mm:ss" -> minutes depuis minuit, pour comparer des horaires sans dépendre du format exact. */
function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

/** Index du gabarit dont la plage [horaireDebut, horaireFin) contient ce créneau (0-based).
 * `null` si aucune plage ne matche (trou entre gabarits, avant le premier, la nuit...) — le
 * Besoin correspondant part alors en HG (pas de "groupe virtuel" : HG *est* le restant), ou si
 * aucun gabarit n'est configuré pour ce centre (fallback sur la découverte dynamique historique). */
function matchGroupTemplateIndex(creneau: string): number | null {
  const templates = sortedGroupTemplates.value
  if (templates.length === 0) return null
  const t = timeToMinutes(creneau)
  for (let i = 0; i < templates.length; i++) {
    const start = timeToMinutes(templates[i].horaireDebut)
    const end = timeToMinutes(templates[i].horaireFin)
    if (t >= start && t < end) return i
  }
  return null
}

// Conditionnements PHYSIQUES connus (Selfix/Blister/Vrac/Tube) — partagés entre Mono, Hétéro
// et Stock. `PARAM_CRITERE_CONDITIONNEMENT` porte aussi "H" (ConditioningMethods.HETERO), un
// mode d'orientation Fabrication (cf. OrientationDoses.vue/OrientationHetero.vue) et non un
// conditionnement physique — celui-là ne doit apparaître dans AUCUN bloc de cet écran : la
// liste ci-dessous sert de filtre explicite, pas juste de source de libellés.
const ALL_PACKAGINGS = computed<{ code3: string; label: string }[]>(() => [
  { code3: ConditioningMethods.SELFIX,  label: t('compteur.packaging.selfix') },
  { code3: ConditioningMethods.BLISTER, label: t('compteur.packaging.blister') },
  { code3: ConditioningMethods.VRAC,    label: t('compteur.packaging.vrac') },
  { code3: ConditioningMethods.TUBE,    label: t('compteur.packaging.tube') },
])

/** Conditionnements physiques (Selfix/Blister/Vrac/Tube) réellement configurés pour la
 * production du centre — jamais "H" (ou un autre code hors de ALL_PACKAGINGS), même si
 * configuré côté centre pour d'autres usages (Fabrication/Orientation). */
const monoConditioningParams = computed(() => {
  const knownCodes = new Set(ALL_PACKAGINGS.value.map((p) => p.code3))
  return centerParameterStore
    .getParameterCritereFamille(PARAM_FAMILLE_PRODUCTION, PARAM_CRITERE_CONDITIONNEMENT)
    .filter((cp) => knownCodes.has(cp.parameter?.code3 ?? ''))
})

/** Clé de groupe "Hors Groupe" : toute ligne qu'aucun groupe ne réclame — alimente le total comme
 * un groupe normal (cf. pushGroupRow) mais n'est rattachée à aucun G1/G2/G3. Un Dispo n'a jamais
 * de créneau côté backend ; son groupe est donc déduit du Besoin qu'il couvre, ou du groupe de
 * monte de son verrat (cf. groupKeyByBoar), et HG ne reçoit que le reste. */
const HG_KEY = '__HG__'

/** boarId sentinelle pour le Dispo sans verrat identifié : aucun rapprochement par verrat
 * possible (2e passe), traité comme 100% surplus dès le départ — jamais un vrai id (négatif). */
const LOOSE_DISPO_BOAR_ID = -1

// Structure intermédiaire du moteur de rapprochement : condKey → clé de groupe → { reserved, standard }.
// Clé de groupe = index de gabarit (0,1,2... si des gabarits sont configurés pour le centre),
// créneau brut (legacy, sans gabarit — cf. matchGroupTemplateIndex), ou HG_KEY si pas de créneau.
type CreneauData = { byColReserved: Map<string, CellVal>; byColStandard: Map<string, CellVal> }
type BesoinEntry = { colKey: string; qty: number; groupKey: string; isReserved: boolean; schemaId: number; volume: number | null; concentration: number | null }
type CondData    = {
  condLabel: string
  creneaux: Map<string, CreneauData>
  // Besoin/Dispo liés à un verrat (boarId) : mis de côté pour rapprochement par verrat (2e
  // passe) puis report du surplus de Dispo non consommé vers d'autres Besoins non couverts
  // du même statut réservé/non-réservé (3e passe), avant de tomber en HG (4e passe).
  // Par verrat puis par colonne : UNE entrée par groupe et statut réservé (cf. addBoarBesoinPart).
  besoinByBoar: Map<number, Map<string, BesoinEntry[]>>
  dispoByBoar: Map<number, Map<string, { reserved: number; standard: number }>>
  // Besoin sans verrat identifié : candidats éligibles au report de surplus (3e passe),
  // puisqu'aucun rapprochement par verrat n'a pu les couvrir en 2e passe.
  looseBesoin: BesoinEntry[]
  // Dispo/Stock d'un verrat SANS AUCUNE commande à son nom (ni spécifique ni générique) — décision
  // 2026-07-27 : avant de rejoindre le groupe de monte de son verrat (HG à défaut, Mantis
  // 0028587), ce volume peut couvrir une commande SANS verrat compatible (même schéma/volume,
  // concentration exigée <= obtenue, fit le plus proche).
  // Indexé par le colKey de la commande sans verrat visée ; dans chaque colonne, les apports sont
  // gardés par verrat et par statut (FreeDispoBucket.sources) pour que le reliquat rejoigne le
  // groupe de monte de son verrat en 4e passe (Mantis 0028587).
  freeDispo: Map<string, FreeDispoBucket>
}

function addToGroup(cond: CondData, groupKey: string, isReserved: boolean, colKey: string, besoin: number, dispo: number): void {
  if (!cond.creneaux.has(groupKey)) cond.creneaux.set(groupKey, { byColReserved: new Map(), byColStandard: new Map() })
  const cr = cond.creneaux.get(groupKey)!
  const target = isReserved ? cr.byColReserved : cr.byColStandard
  if (!target.has(colKey)) target.set(colKey, emptyCell())
  const cell = target.get(colKey)!
  cell.besoin += besoin
  cell.dispo  += dispo
}

/**
 * Moteur de rapprochement Besoin/Dispo par verrat (4 passes), générique par segment (Mono pour
 * le bloc gris, HeteroStandard/HeteroArmada pour les blocs Hétéro) — même mécanique partout, cf.
 * décision produit "même mécanisme complet que le bloc gris" pour Hétéro (2026-07-09). `initialConds`
 * force l'affichage des conditionnements configurés même sans Besoin/Dispo pour la date affichée.
 */
function computeSegmentGroups(segment: string, initialConds: { condKey: string; condLabel: string }[]): Map<string, CondData> {
  const condMap = new Map<string, CondData>()
  // Blocs Hétéro (HeteroStandard / HeteroArmada) : pas de ligne « Hors Groupe », cf. plus bas.
  const isHeteroSegment = segment !== 'Mono'

  function getOrCreateCond(condKey: string, condLabel: string): CondData {
    let cond = condMap.get(condKey)
    if (!cond) {
      cond = { condLabel, creneaux: new Map(), besoinByBoar: new Map(), dispoByBoar: new Map(), looseBesoin: [], freeDispo: new Map() }
      condMap.set(condKey, cond)
    }
    return cond
  }

  initialConds.forEach(({ condKey, condLabel }) => getOrCreateCond(condKey, condLabel))

  // Gabarits configurés pour ce centre (paramétrage "Groupe de départ") : s'ils existent, ils
  // pilotent le regroupement G1/G2/G3 des Besoins par plage horaire plutôt que par découverte
  // dynamique des créneaux du jour.
  const templates = sortedGroupTemplates.value
  const useTemplates = templates.length > 0

  /** Groupe de monte porté par une ligne Baseline -> clé de groupe du moteur de rapprochement.
   * Baseline porte `creneau = "G{Number}"` (numéro du groupe de monte, cf. CompteurRebuildService)
   * et non un horaire : `matchGroupTemplateIndex` ne s'applique donc pas. L'invariant posé par
   * GroupMountingService.AssignTemplateAsync est `GroupMounting.Number == GroupTemplate.Rank` —
   * on retrouve le gabarit par son rang, et sa position dans `sortedGroupTemplates` EST la clé
   * de groupe (celle que `greyRows` étiquette `G{idx + 1}`). Recherche par rang plutôt que
   * `Number - 1` : les rangs ne sont pas forcément 1..N contigus, et un groupe sans gabarit
   * correspondant (constaté en base : un "G7" pour 4 gabarits configurés) doit rester en HG
   * plutôt que d'inventer une ligne de groupe inexistante. */
  function groupKeyFromMountingGroup(creneau: string): string | null {
    const number = Number(creneau.replace(/^G/i, ''))
    if (!Number.isFinite(number)) return null
    const idx = templates.findIndex((tpl) => tpl.rank === number)
    return idx >= 0 ? String(idx) : null
  }

  /** Groupe de monte de chaque verrat, lu sur les lignes Baseline (plan de monte, une ligne par
   * attache verrat↔groupe). Le Dispo/Stock n'a JAMAIS de créneau côté backend (`Creneau = null`) :
   * sans cette table de correspondance, la production d'un verrat pourtant affecté à un groupe
   * tombait systématiquement en HG (Mantis 0028296). Un blend hétéro a `BoarId = null`, il n'est
   * donc jamais concerné. */
  const groupKeyByBoar = new Map<number, string>()
  if (useTemplates) {
    for (const c of datas.value) {
      if (c.nature !== 'Baseline' || c.boarId == null || !c.creneau) continue
      if (groupKeyByBoar.has(c.boarId)) continue
      const key = groupKeyFromMountingGroup(c.creneau)
      if (key != null) groupKeyByBoar.set(c.boarId, key)
    }
  }

  /** Clé de groupe pour une ligne SANS rapprochement par verrat possible (boarId absent). */
  function directGroupKey(c: { nature: string; creneau?: string | null }): string {
    if (!c.creneau) return HG_KEY
    if (useTemplates && c.nature === 'Besoin') {
      const idx = matchGroupTemplateIndex(c.creneau)
      return idx != null ? String(idx) : HG_KEY
    }
    return c.creneau
  }

  /** Colonne "générique" d'un schéma (sans volume/concentration précis) — cf. 2e sous-passe ci-dessous. */
  function genericColKeyOf(schemaId: number): string {
    return `${schemaId}____`
  }

  // 1ère passe (a) : collecte des Besoin. Fait AVANT les Dispo pour connaître, par verrat, les
  // colonnes "spécifiques" (volume/concentration précis) réellement demandées — cf. 1ère passe (b).
  for (const c of datas.value) {
    // cf. E30-Mapping-Ecran-Compteurs §2 : sans ce filtre, Stock/Attente/Baseline/
    // ParcPreleve/segment(s) hors périmètre (qui portent aussi schemaId) créent des lignes
    // cond/Gx parasites (vides côté valeur, mais visibles dans la grille).
    if (c.nature !== 'Besoin') continue
    if ((c.segment ?? 'Mono') !== segment) continue
    if (c.schemaId == null) continue

    const condId   = c.conditionnementId ?? null
    const condKey  = String(condId ?? '')
    const vol      = c.volume        ?? null
    const conc     = c.concentration ?? null
    const colKey   = `${c.schemaId}__${vol ?? ''}__${conc ?? ''}`

    const condLabel = condId != null
      ? (parameterStore.getParameterById(condId)?.value ?? `#${condId}`)
      : '—'
    const cond = getOrCreateCond(condKey, condLabel)

    const entry: BesoinEntry = { colKey, qty: Math.abs(c.quantite), groupKey: directGroupKey(c), isReserved: c.isReserved === true, schemaId: c.schemaId, volume: vol, concentration: conc }
    if (c.boarId != null) {
      let byCol = cond.besoinByBoar.get(c.boarId)
      if (!byCol) { byCol = new Map(); cond.besoinByBoar.set(c.boarId, byCol) }
      // Deux commandes du même verrat sur la même colonne mais à des horaires de groupes
      // différents restent DEUX entrées : les fusionner gardait le groupe de la première et vidait
      // l'autre (question de Quentin, recette du 30/09/2026).
      const parts = byCol.get(colKey) ?? []
      addBoarBesoinPart(parts, entry)
      byCol.set(colKey, parts)
    } else {
      // Pas de verrat identifié : aucun rapprochement par verrat possible (2e passe), mais
      // reste candidat au report de surplus d'un autre verrat (3e passe).
      cond.looseBesoin.push(entry)
    }
  }

  // 1ère passe (b) : collecte de l'offre disponible pour couvrir un Besoin — Dispo (orienté, pas
  // encore validé) ET Stock (déjà validé/physiquement en stock : un dispatch validé disparaît de
  // Dispo mais reste une offre valable pour couvrir une commande, sinon une commande couverte par
  // du stock réapparaît comme non couverte). Même mécanique pour
  // les deux natures : un Dispo/Stock n'a de colonne "spécifique" (son propre volume/concentration)
  // QUE si son verrat a une commande sur EXACTEMENT cette colonne — sinon il est rattaché à la
  // colonne générique du schéma, pour rester rapprochable (2e passe) avec les commandes génériques
  // de ce verrat (décision 2026-07-20 : une commande n'a quasiment jamais de volume/concentration
  // précis, contrairement à un dispatch/stock qui en a toujours un réel).
  for (const c of datas.value) {
    // REQ904 : mode anticipation actif -> aucune offre (Dispo/Stock) ne couvre les commandes,
    // on ne veut voir que les commandes/plan de monte. Les lignes Dispo/Stock elles-mêmes restent
    // affichées ailleurs (bloc STOCK, compteurs "orienté") : seul ce rapprochement les ignore.
    if (anticipationMode.value) continue
    if (c.nature !== 'Dispo' && c.nature !== 'Stock') continue
    if ((c.segment ?? 'Mono') !== segment) continue
    if (c.schemaId == null) continue

    const condId   = c.conditionnementId ?? null
    const condKey  = String(condId ?? '')
    const vol      = c.volume        ?? null
    const conc     = c.concentration ?? null
    const genericColKey  = genericColKeyOf(c.schemaId)

    const condLabel = condId != null
      ? (parameterStore.getParameterById(condId)?.value ?? `#${condId}`)
      : '—'
    const cond = getOrCreateCond(condKey, condLabel)

    // Priorité de rapprochement d'un Dispo/Stock (décision produit 2026-07-27) :
    //  1. Commande de CE verrat avec spécificité (volume/concentration, fit le plus proche >=) ;
    //  2. Commande de CE verrat SANS spécificité (colonne générique du schéma — même colonne que 4,
    //     donc pas de traitement distinct nécessaire) ;
    //  3. Commande SANS verrat mais avec spécificité compatible (même schéma/volume, fit le plus
    //     proche >=) — seulement si ce verrat n'a AUCUNE commande à son nom (1 et 2 absentes) ;
    //  4. Colonne générique du schéma (aucune commande compatible trouvée).
    // Dispo : jamais de créneau réel côté backend -> son groupe vient soit du Besoin qu'il couvre
    // (2e/3e passes), soit, à défaut de tout Besoin, du groupe de monte de son verrat
    // (`groupKeyByBoar`, Mantis 0028296) ; HG en dernier recours.
    // Arbitrage du 22/09/2026 : une commande SANS volume exigé se sert au volume par défaut du
    // centre — une dose d'un autre volume ne la couvre pas (cf. dispoCoversGenericBesoin). Vaut
    // pour la commande générique du verrat (tier 2) comme pour celle sans verrat (tier 3 bis).
    const coversGeneric = dispoCoversGenericBesoin(vol, defaultBesoinVolume.value)
    const hasLooseGenericBesoin = cond.looseBesoin.some((lb) => lb.colKey === genericColKey)

    if (c.boarId != null) {
      const besoinCols = cond.besoinByBoar.get(c.boarId)
      const tier1Key = besoinCols
        ? bestFitConcentrationKey(
            [...besoinCols.values()].map((parts) => parts[0]).map((e) => ({ key: e.colKey, volume: e.volume, concentration: e.concentration })),
            vol, conc,
          )
        : null
      const hasOwnGenericOrder = besoinCols?.has(genericColKey) === true

      // Dose d'un AUTRE volume que celui auquel se sert la commande générique de son verrat :
      // elle ne la couvre pas. Le rapprochement de la 2e passe se fait par (verrat, colonne) et
      // la colonne reste la générique : la seule façon de ne pas solder la commande est de sortir
      // cette dose du circuit de rapprochement. Elle reste affichée dans SA colonne, sur la ligne
      // du groupe de monte de son verrat (HG à défaut) — même traitement qu'une production non
      // liée à une commande (Mantis 0028296).
      if (!tier1Key && hasOwnGenericOrder && !coversGeneric) {
        addToGroup(cond, groupKeyByBoar.get(c.boarId) ?? HG_KEY, false, genericColKey, 0, c.quantite)
        continue
      }

      let colKey: string
      let isFreeForLoose = false
      if (tier1Key) {
        colKey = tier1Key
      } else if (hasOwnGenericOrder) {
        colKey = genericColKey
      } else {
        // Ce verrat n'a AUCUNE commande à son nom : tente une commande sans verrat compatible
        // avant de tomber en surplus pur (cf. freeDispo, consommé en pass 3/4).
        const looseCandidates = cond.looseBesoin.filter((lb) => lb.schemaId === c.schemaId && lb.volume != null)
        const tier3Key = bestFitConcentrationKey(looseCandidates.map((lb) => ({ key: lb.colKey, volume: lb.volume, concentration: lb.concentration })), vol, conc)
        // Tier 3 bis : commande SANS verrat et SANS volume exigé — le cas courant, Ogicom laissant
        // `dose_vol` à 0. `bestFitConcentrationKey` ne peut pas la retenir (elle n'a ni volume ni
        // concentration à comparer), si bien qu'AUCUNE dose ne la couvrait jamais : le compteur
        // affichait un besoin que l'orientation montrait soldé par le stock. Elle est désormais
        // couverte par les doses au volume par défaut, via le pool générique de sa colonne.
        const tier3BisKey = !tier3Key && hasLooseGenericBesoin && coversGeneric ? genericColKey : null
        colKey = tier3Key ?? tier3BisKey ?? genericColKey
        isFreeForLoose = tier3Key != null || tier3BisKey != null
      }

      if (isFreeForLoose) {
        // Verrat sans commande à son nom : réservé ou non, sa production couvre le classique
        // (arbitrage 0028587, cf. addFreeDispo).
        const bucket = cond.freeDispo.get(colKey) ?? emptyFreeDispo()
        addFreeDispo(bucket, c.boarId, c.quantite, c.isReserved === true, false)
        cond.freeDispo.set(colKey, bucket)
      } else {
        let byCol = cond.dispoByBoar.get(c.boarId)
        if (!byCol) { byCol = new Map(); cond.dispoByBoar.set(c.boarId, byCol) }
        const bucket = byCol.get(colKey) ?? { reserved: 0, standard: 0 }
        if (c.isReserved === true) bucket.reserved += c.quantite
        else bucket.standard += c.quantite
        byCol.set(colKey, bucket)
      }
    } else {
      // Pas de verrat : même tentative de couverture d'une commande sans verrat compatible
      // (tier 3) avant de tomber sur le pool générique (aucun rapprochement par verrat possible
      // de toute façon, cf. 3e/4e passes, via le boarId sentinelle dédié).
      const looseCandidates = cond.looseBesoin.filter((lb) => lb.schemaId === c.schemaId && lb.volume != null)
      const tier3Key = bestFitConcentrationKey(looseCandidates.map((lb) => ({ key: lb.colKey, volume: lb.volume, concentration: lb.concentration })), vol, conc)

      // Hétéro (arbitrage MOE, Mantis 0028305/0028306) : le dispo d'un blend n'a pas de verrat et
      // les commandes de groupe sont génériques, donc le tier 3 ne matche jamais — il partait en HG
      // alors qu'un besoin de groupe l'attendait. Cf. loosePoolKeyForDispo.
      // En mono, même tier 3 bis que ci-dessus : une commande sans verrat ni volume exigé est
      // couverte par les doses au volume par défaut du centre.
      const poolKey = loosePoolKeyForDispo(tier3Key, isHeteroSegment, genericColKey, hasLooseGenericBesoin && coversGeneric)

      if (poolKey) {
        const bucket = cond.freeDispo.get(poolKey) ?? emptyFreeDispo()
        addFreeDispo(bucket, LOOSE_DISPO_BOAR_ID, c.quantite, c.isReserved === true, true)
        cond.freeDispo.set(poolKey, bucket)
      } else {
        let byCol = cond.dispoByBoar.get(LOOSE_DISPO_BOAR_ID)
        if (!byCol) { byCol = new Map(); cond.dispoByBoar.set(LOOSE_DISPO_BOAR_ID, byCol) }
        const bucket = byCol.get(genericColKey) ?? { reserved: 0, standard: 0 }
        if (c.isReserved === true) bucket.reserved += c.quantite
        else bucket.standard += c.quantite
        byCol.set(genericColKey, bucket)
      }
    }
  }

  // 2e passe : rapprochement Besoin/Dispo par verrat (même boarId, même conditionnement/colonne).
  // Le Dispo couvre le Besoin du même verrat à due concurrence, dans le groupe du Besoin (ex.
  // G1 réservé net à 0 si Dispo >= Besoin). Le surplus non consommé (remainder) est mis de côté,
  // classé par GROUPE D'ORIGINE du Besoin couvert + statut réservé/non-réservé DU DISPO — un
  // surplus ne peut reporter que dans SON PROPRE groupe (ex. le surplus d'un verrat dont la
  // commande était en G1 ne peut pas couvrir un Besoin non couvert en G2) — consommé standard
  // en priorité, puis réservé.
  type SurplusPool = Map<string, number> // "colKey|groupKey|reserved" ou "...|standard" -> quantité
  const surplusByCondKey = new Map<CondData, SurplusPool>()

  for (const cond of condMap.values()) {
    const surplus: SurplusPool = new Map()
    surplusByCondKey.set(cond, surplus)

    const boarIds = new Set([...cond.besoinByBoar.keys(), ...cond.dispoByBoar.keys()])
    for (const boarId of boarIds) {
      const besoinCols = cond.besoinByBoar.get(boarId)
      const dispoCols  = cond.dispoByBoar.get(boarId)
      const colKeys = new Set([...(besoinCols?.keys() ?? []), ...(dispoCols?.keys() ?? [])])
      for (const colKey of colKeys) {
        const parts = besoinCols?.get(colKey)
        const d = dispoCols?.get(colKey) ?? { reserved: 0, standard: 0 }
        const totalDispo = d.reserved + d.standard

        if (!parts || parts.length === 0) {
          // Verrat sans AUCUN Besoin pour cette colonne : rien à couvrir, rien à reporter. Sa
          // production va dans la ligne de SON groupe de monte dès que le plan lui en attribue un
          // (Mantis 0028296) ; à défaut — verrat hors plan de monte, groupe sans gabarit
          // correspondant, ou Dispo sans verrat identifié (LOOSE_DISPO_BOAR_ID) — elle reste en HG,
          // production "libre" non liée à une commande (décision produit 2026-07-20).
          if (totalDispo > 0) addToGroup(cond, groupKeyByBoar.get(boarId) ?? HG_KEY, false, colKey, 0, totalDispo)
          continue
        }

        // Les commandes du verrat sur cette colonne sont couvertes groupe par groupe, G1 d'abord ;
        // dans l'offre, le standard est consommé avant le réservé. Le reliquat part au pool du
        // groupe de la dernière commande couverte (3e passe).
        const { coverage, remainder, remainderGroupKey } = coverBoarBesoinsInGroupOrder(parts, d)
        for (const { part, covered } of coverage) addToGroup(cond, part.groupKey, part.isReserved, colKey, part.qty, covered)

        const remainderStandard = remainder.standard
        const remainderReserved = remainder.reserved
        const poolKeyStandard = `${colKey}|${remainderGroupKey}|standard`
        const poolKeyReserved = `${colKey}|${remainderGroupKey}|reserved`
        if (remainderStandard > 0) surplus.set(poolKeyStandard, (surplus.get(poolKeyStandard) ?? 0) + remainderStandard)
        if (remainderReserved > 0) surplus.set(poolKeyReserved, (surplus.get(poolKeyReserved) ?? 0) + remainderReserved)
      }
    }
  }

  // 3e passe : le surplus de Dispo non consommé par son propre verrat comble en priorité
  // d'AUTRES Besoins non couverts du MÊME groupe/colonne/statut réservé (jamais un autre
  // groupe) — complété par le "dispo libre" (tier 3 : verrats sans commande propre, décision
  // 2026-07-27), consommé APRÈS le surplus verrat (celui-ci reste prioritaire). Ce qui reste
  // est arbitré en 4e passe (ci-dessous) : le surplus rejoint son groupe, le dispo libre le
  // groupe de monte de chaque verrat qui l'a apporté, HG à défaut (Mantis 0028587).
  for (const cond of condMap.values()) {
    const surplus = surplusByCondKey.get(cond)!
    const candidates = [...cond.looseBesoin].sort((a, b) => compareGroupKeys(a.groupKey, b.groupKey))
    const uncovered = new Map<BesoinEntry, number>()
    for (const entry of candidates) {
      const statusKey = entry.isReserved ? 'reserved' : 'standard'
      const poolKey = `${entry.colKey}|${entry.groupKey}|${statusKey}`
      const surplusAvailable = surplus.get(poolKey) ?? 0
      const free = cond.freeDispo.get(entry.colKey) ?? emptyFreeDispo()
      const freeAvailable = entry.isReserved ? free.reserved : free.standard

      const allocated = Math.min(surplusAvailable + freeAvailable, entry.qty)
      addToGroup(cond, entry.groupKey, entry.isReserved, entry.colKey, entry.qty, allocated)
      if (allocated < entry.qty) uncovered.set(entry, entry.qty - allocated)

      const fromSurplus = Math.min(allocated, surplusAvailable)
      const fromFree = allocated - fromSurplus
      if (surplusAvailable > 0) surplus.set(poolKey, surplusAvailable - fromSurplus)
      if (fromFree > 0) {
        if (entry.isReserved) free.reserved -= fromFree
        else free.standard -= fromFree
        consumeFreeDispoSources(free.sources, fromFree, entry.isReserved)
        cond.freeDispo.set(entry.colKey, free)
      }
    }

    // 3e passe bis (Mantis 0028612) : une commande générique (sans volume exigé) encore non couverte
    // prend le surplus des colonnes « volume seul » du même schéma et du même groupe dont le volume
    // la sert (cf. genericBesoinSurplusPoolKeys). Après la 3e passe, pour que les besoins de la
    // colonne d'origine du surplus restent servis en premier. Une commande standard y prend aussi
    // le surplus réservé (réservation active du verrat, arbitrages 0028612 / 0028587) ; une
    // commande à concentration exigée n'y prend rien (le helper lit `entry.concentration`).
    // Dans un groupe, les réservées passent avant les standard (cf. allocateGenericBesoinSurplus).
    for (const { entry, taken } of allocateGenericBesoinSurplus(candidates, uncovered, surplus, defaultBesoinVolume.value)) {
      addToGroup(cond, entry.groupKey, entry.isReserved, entry.colKey, 0, taken)
    }
  }

  // 4e passe : ce que plus aucun Besoin ne reprend.
  //  - Le reliquat de surplus retombe dans SON groupe (arbitrage métier 2026-08-17), celui de la
  //    commande dont il provient — déjà porté par la clé de pool. Il partait en HG, ce qui sortait
  //    du groupe une production pourtant rattachée à une commande de ce groupe (Mantis 0028296).
  //  - Le "dispo libre" (tier 3) rejoint le groupe de monte de chaque verrat qui l'a apporté, HG à
  //    défaut (Mantis 0028587) : ses apports sont suivis par verrat (cf. FreeDispoBucket). Il
  //    partait en bloc en HG, ce qui sortait de son groupe la production d'un verrat du plan.
  for (const cond of condMap.values()) {
    const surplus = surplusByCondKey.get(cond)!
    for (const [poolKey, qty] of surplus) {
      if (qty <= 0) continue
      // Clé de pool = `${colKey}|${groupKey}|${statut}` ; aucun des trois ne contient de '|'
      // (colKey = `schemaId__vol__conc`, groupKey = index de gabarit, créneau brut ou HG_KEY).
      const [colKey, groupKey] = poolKey.split('|')
      // Toujours sur la ligne "Gx" standard, jamais "Gx réservé" : byColReserved n'est alimenté
      // que par une vraie commande réservée, sinon on ferait apparaître une ligne réservée sans
      // commande derrière (cf. pushGroupRow).
      addToGroup(cond, groupKey, false, colKey, 0, qty)
    }
    // Hétéro (arbitrage MOE, Mantis 0028305/0028306) : pas de « Hors Groupe ». Ce que les besoins
    // ne reprennent pas — l'excédent — rejoint le PREMIER groupe qui porte un besoin sur cette
    // colonne (G1 s'il en a un, sinon G2…). Le remplissage ordonné G1 puis G2 est déjà assuré par
    // la 3e passe, qui parcourt les besoins triés par groupe.
    // À défaut de tout besoin sur la colonne, repli sur le premier groupe du plan de monte : il n'y
    // a pas de « premier groupe des besoins », mais les doses doivent rester visibles.
    const besoinsTries = isHeteroSegment
      ? [...cond.looseBesoin].sort((a, b) => compareGroupKeys(a.groupKey, b.groupKey))
      : []
    const heteroFallbackGroup = sortedGroupTemplates.value.length > 0 ? '0' : HG_KEY

    for (const [colKey, free] of cond.freeDispo) {
      if (!isHeteroSegment) {
        for (const [groupKey, qty] of freeDispoLeftoverByGroup(free.sources, (boarId) => groupKeyByBoar.get(boarId), HG_KEY)) {
          addToGroup(cond, groupKey, false, colKey, 0, qty)
        }
        continue
      }
      const target = excessGroupKey(besoinsTries, colKey, HG_KEY, heteroFallbackGroup)
      if (free.standard > 0) addToGroup(cond, target, false, colKey, 0, free.standard)
      if (free.reserved > 0) addToGroup(cond, target, false, colKey, 0, free.reserved)
    }
  }

  return condMap
}

const greyRows = computed<(CondRow | GroupRow)[]>(() => {
  // Ordre de configuration du centre (cf. E30-Compteur mockup : Selfix avant Blister,
  // pas un ordre alphabétique) — les conditionnements hors config (trouvés uniquement
  // dans les données) sont ajoutés à la fin, triés alphabétiquement.
  const orderIndex = new Map<string, number>()
  const initialConds = monoConditioningParams.value.map((cp, idx) => {
    const condKey = String(cp.parameterId)
    orderIndex.set(condKey, idx)
    return { condKey, condLabel: cp.parameter?.value ?? `#${cp.parameterId}` }
  })
  const condMap = computeSegmentGroups('Mono', initialConds)

  const templates = sortedGroupTemplates.value
  const useTemplates = templates.length > 0

  // Pas de bandeau "Mono" : chaque conditionnement (Selfix, Blister...) est directement
  // son propre bloc gris, cf. maquette Documents/Lot9/Haut tableau compteurs.png.
  const rows: (CondRow | GroupRow)[] = []

  // Trier selon l'ordre configuré du centre (fallback alphabétique pour les conditionnements
  // hors config, trouvés uniquement dans les données).
  for (const [condKey, cond] of [...condMap.entries()].sort(([ka, a], [kb, b]) => {
    const ia = orderIndex.get(ka)
    const ib = orderIndex.get(kb)
    if (ia != null && ib != null) return ia - ib
    if (ia != null) return -1
    if (ib != null) return 1
    return a.condLabel.localeCompare(b.condLabel)
  })) {
    const encartId = `mono:${condKey}`

    // Agréger toutes les valeurs pour l'en-tête conditionnement
    const condByCol = new Map<string, CellVal>()
    const groupRows: GroupRow[] = []

    function pushGroupRow(gLabel: string, cr: CreneauData): void {
      // Ligne "Gx réservé" — affichée seulement s'il y a au moins une commande réservée dans ce
      // groupe (byColReserved n'est alimenté que par une vraie commande isReserved=true, cf. 2e/3e
      // passes de computeSegmentGroups) : pas de ligne vide en l'absence de commande.
      if (cr.byColReserved.size > 0) {
        groupRows.push({ _type: 'group', label: t('compteur.rows.groupReserved', { group: gLabel }), isReserved: true, byCol: cr.byColReserved, encartId, isEncartHeader: false })
      }
      // Ligne "Gx" standard — toujours affichée
      groupRows.push({ _type: 'group', label: gLabel, isReserved: false, byCol: cr.byColStandard, encartId, isEncartHeader: false })

      for (const [k, cell] of [...cr.byColReserved, ...cr.byColStandard]) {
        if (!condByCol.has(k)) condByCol.set(k, emptyCell())
        const tc = condByCol.get(k)!
        tc.besoin += cell.besoin
        tc.dispo  += cell.dispo
      }
    }

    /** Pas de distinction "réservé" pour HG (contrairement aux Gx) : une seule ligne, qui
     * fusionne réservé+standard — cf. "il n'y a pas de HG réservé". */
    function pushSingleGroupRow(gLabel: string, cr: CreneauData): void {
      const merged = new Map<string, CellVal>()
      for (const [k, cell] of [...cr.byColReserved, ...cr.byColStandard]) {
        if (!merged.has(k)) merged.set(k, emptyCell())
        const m = merged.get(k)!
        m.besoin += cell.besoin
        m.dispo  += cell.dispo
      }
      groupRows.push({ _type: 'group', label: gLabel, isReserved: false, byCol: merged, encartId, isEncartHeader: false })

      for (const [k, cell] of merged) {
        if (!condByCol.has(k)) condByCol.set(k, emptyCell())
        const tc = condByCol.get(k)!
        tc.besoin += cell.besoin
        tc.dispo  += cell.dispo
      }
    }

    // "Hors Groupe" (HG) — toujours affichée EN PREMIER, sans distinction réservé/standard :
    // capture tout ce qui n'a pas de créneau réel (Dispo, systématiquement) ; alimente le
    // total comme les autres lignes.
    pushSingleGroupRow('HG', cond.creneaux.get(HG_KEY) ?? { byColReserved: new Map(), byColStandard: new Map() })

    if (useTemplates) {
      // Une ligne par gabarit configuré (G1, G2, G3...), toujours affichée même sans Besoin
      // ce jour-là — cohérent avec le principe "toujours afficher les éléments configurés".
      // Pas de "groupe virtuel" pour le restant hors plage : HG *est* le restant (cf. HG_KEY).
      templates.forEach((_, idx) => {
        const cr = cond.creneaux.get(String(idx)) ?? { byColReserved: new Map(), byColStandard: new Map() }
        pushGroupRow(`G${idx + 1}`, cr)
      })
    } else {
      // Legacy (aucun gabarit configuré pour ce centre) : découverte dynamique par créneau brut
      // (hors HG, déjà traité ci-dessus), trié chronologiquement et numéroté G1, G2, G3...
      const sortedCreneaux = [...cond.creneaux.entries()]
        .filter(([k]) => k !== HG_KEY)
        .sort(([a], [b]) => a.localeCompare(b))
      sortedCreneaux.forEach(([, cr], idx) => pushGroupRow(`G${idx + 1}`, cr))
    }

    rows.push({ _type: 'cond', label: cond.condLabel, byCol: condByCol, encartId, isEncartHeader: true })
    rows.push(...groupRows)
  }

  return rows
})

// ---------------------------------------------------------------------------
// Conditionnements (Selfix/Blister/Vrac) — partagés entre Hétéro et Stock.
// ---------------------------------------------------------------------------

/** Conditionnements physiques réellement configurés pour la production du centre — même
 * source (déjà filtrée sur ALL_PACKAGINGS) que monoConditioningParams (bloc Mono) : un
 * conditionnement non configuré (ex. Blister si le centre n'en fait pas) n'apparaît pas ici
 * non plus, ni "H"/"T" ou autre code hors ALL_PACKAGINGS. */
const PACKAGINGS = computed(() => {
  const configuredCodes = new Set(monoConditioningParams.value.map((cp) => cp.parameter?.code3))
  return ALL_PACKAGINGS.value.filter((p) => configuredCodes.has(p.code3))
})

/** id paramètre conditionnement (PRODUCTION/CONDITIONNEMENT) → {code3, label}. */
const packagingByParamId = computed(() => {
  const m = new Map<number, { code3: string; label: string }>()
  for (const pack of PACKAGINGS.value) {
    const id = parameterStore.getParameterByCode3('PRODUCTION', 'CONDITIONNEMENT', pack.code3)?.id
    if (id != null) m.set(id, pack)
  }
  return m
})

/** Sens inverse : code3 → id paramètre, pour reconstruire les clés externes des getters store (`${segment}__${condId}`). */
const packagingIdByCode3 = computed(() => {
  const m = new Map<string, number>()
  for (const [id, pack] of packagingByParamId.value) m.set(pack.code3, id)
  return m
})

// ---------------------------------------------------------------------------
// Blocs Hétéro Standard / Hétéro Armada (U06) — cf. E30-Mapping-Ecran-Compteurs §3.
// ---------------------------------------------------------------------------
// Même moteur de rapprochement Besoin/Dispo par verrat que le bloc gris Mono (cf.
// computeSegmentGroups), paramétré sur le segment HeteroStandard/HeteroArmada — décision
// produit 2026-07-09 ("même mécanisme complet que le bloc gris" suite au cas "AB - Blister").
// Seule différence d'affichage : pas de distinction réservé/standard sur HG/G1/G2 ("une seule
// ligne G1/G2/HG, pas de split").

function buildHeteroRows(
  variant: HeteroVariant,
  segment: 'HeteroStandard' | 'HeteroArmada',
  sectionLabel: string,
  dosesByCol: Map<string, number>,
  verratsByCol: Map<string, number>,
  // Sous-ensemble STRICT de `dosesByCol`, réservé à l'AFFICHAGE de la ligne « Nbre de doses
  // orientées » (et à son détail au clic) : `dosesByCol` reste le total complet, nécessaire au
  // bandeau de section (Mantis 0028304, cf. plus bas) — voir compteur.store.ts pour la décision.
  dosesByColAvantLot: Map<string, number>,
): (HeteroSectionRow | CounterRow | HeteroCondRow | HeteroGroupRow)[] {
  const encartId = `hetero:${variant}`
  const prefix = variant === 'standard' ? 'H' : 'A'

  const initialConds = PACKAGINGS.value.map((pack) => {
    const condId = packagingIdByCode3.value.get(pack.code3)
    return { condKey: condId != null ? String(condId) : `__missing__${pack.code3}`, condLabel: pack.label }
  })
  const condMap = computeSegmentGroups(segment, initialConds)

  const templates = sortedGroupTemplates.value
  const useTemplates = templates.length > 0

  // Total du bandeau de section : somme de tous les conditionnements (Selfix+Blister+Vrac...)
  // de la variante — même principe que le bandeau "Stock" (orange), qui agrège déjà tous ses
  // conditionnements/segments — PLUS les doses orientées pas encore conditionnées (cf. plus bas).
  // Rempli au fil de la boucle ci-dessous.
  const sectionByCol = new Map<string, CellVal>()
  function addToSection(k: string, cell: CellVal): void {
    if (!sectionByCol.has(k)) sectionByCol.set(k, emptyCell())
    const s = sectionByCol.get(k)!
    s.besoin += cell.besoin
    s.dispo  += cell.dispo
  }

  const counterDetailPredicate = isOrienteHeteroAvantLot(segment)
  const rows: (HeteroSectionRow | CounterRow | HeteroCondRow | HeteroGroupRow)[] = [
    { _type: 'heteroSection', variant, label: sectionLabel, byCol: sectionByCol, encartId, isEncartHeader: true },
    { _type: 'counter', variant, label: t('compteur.rows.nbDosesOrientees'), byCol: dosesByColAvantLot, encartId, isEncartHeader: false, detailPredicate: counterDetailPredicate },
    { _type: 'counter', variant, label: t('compteur.rows.nbVerratsOrientes'), byCol: verratsByCol, encartId, isEncartHeader: false, detailPredicate: counterDetailPredicate, isDistinctCount: true },
  ]

  for (const pack of PACKAGINGS.value) {
    const condId  = packagingIdByCode3.value.get(pack.code3)
    const condKey = condId != null ? String(condId) : `__missing__${pack.code3}`
    const cond = condMap.get(condKey) ?? { condLabel: pack.label, creneaux: new Map(), besoinByBoar: new Map(), dispoByBoar: new Map(), looseBesoin: [] }

    const condByCol = new Map<string, CellVal>()
    const groupRows: HeteroGroupRow[] = []

    /** Pas de distinction réservé/standard côté Hétéro (contrairement au bloc gris) : une
     * seule ligne par groupe, réservé+standard fusionnés — cf. "une seule ligne G1/G2/HG". */
    function pushHeteroGroupRow(gLabel: string, cr: CreneauData): void {
      const merged = new Map<string, CellVal>()
      for (const [k, cell] of [...cr.byColReserved, ...cr.byColStandard]) {
        if (!merged.has(k)) merged.set(k, emptyCell())
        const m = merged.get(k)!
        m.besoin += cell.besoin
        m.dispo  += cell.dispo
      }
      groupRows.push({ _type: 'heteroGroup', variant, label: gLabel, byCol: merged, encartId, isEncartHeader: false })

      for (const [k, cell] of merged) {
        if (!condByCol.has(k)) condByCol.set(k, emptyCell())
        const tc = condByCol.get(k)!
        tc.besoin += cell.besoin
        tc.dispo  += cell.dispo
      }
    }

    // Pas de « Hors Groupe » en hétéro (arbitrage MOE, Mantis 0028305/0028306) : les doses
    // orientées rejoignent désormais toutes un groupe (cf. 4e passe de computeSegmentGroups).
    // La ligne n'est conservée que si elle porte encore quelque chose — cas résiduel d'un BESOIN
    // sans créneau, que directGroupKey classe en HG : la masquer ferait disparaître ce besoin du
    // tableau, ce qui serait pire que de l'afficher hors groupe.
    const hgCreneau = cond.creneaux.get(HG_KEY)
    if (hgCreneau && (hgCreneau.byColReserved.size > 0 || hgCreneau.byColStandard.size > 0))
      pushHeteroGroupRow('HG', hgCreneau)

    if (useTemplates) {
      templates.forEach((_, idx) => {
        const cr = cond.creneaux.get(String(idx)) ?? { byColReserved: new Map(), byColStandard: new Map() }
        pushHeteroGroupRow(`G${idx + 1}`, cr)
      })
    } else {
      // Legacy (aucun gabarit configuré pour ce centre) : découverte dynamique par créneau brut
      // (hors HG, déjà traité ci-dessus), trié chronologiquement et numéroté G1, G2, G3...
      const sortedCreneaux = [...cond.creneaux.entries()]
        .filter(([k]) => k !== HG_KEY)
        .sort(([a], [b]) => a.localeCompare(b))
      sortedCreneaux.forEach(([, cr], idx) => pushHeteroGroupRow(`G${idx + 1}`, cr))
    }

    rows.push({ _type: 'heteroCond', variant, label: `${prefix}${pack.code3} - ${pack.label}`, byCol: condByCol, encartId, isEncartHeader: false })
    rows.push(...groupRows)

    for (const [k, cell] of condByCol) addToSection(k, cell)
  }

  // Les doses ORIENTÉES pas encore conditionnées entrent aussi dans le total de la section
  // (Mantis 0028304) : elles couvrent déjà une partie du besoin, donc le bandeau doit afficher
  // le besoin NET. Exemple du modèle : besoin -55 (AS Selfix -45 + AB Blister -10), 30 doses
  // orientées -> bandeau à -25 et non -55.
  // Elles ne sont dans AUCUNE ligne de conditionnement, et il n'y a donc pas de double compte :
  // `dosesByCol` ne porte que les lignes SANS conditionnement (cf. orienteDosesHetero*), or la
  // boucle ci-dessus n'agrège que les conditionnements de PACKAGINGS. Comptées en `dispo`, du
  // côté de l'offre : la valeur affichée est dispo - besoin.
  // Volontairement `dosesByCol` (total complet) et non `dosesByColAvantLot` : un lot hétéro créé
  // non conditionné couvre toujours le besoin, même si ses doses ne s'affichent plus dans la ligne
  // « Nbre de doses orientées » (cf. décision 2026-08-27 sur ce getter).
  for (const [k, doses] of dosesByCol) addToSection(k, { besoin: 0, dispo: doses })

  return rows
}

const heteroStandardRows = computed<(HeteroSectionRow | CounterRow | HeteroCondRow | HeteroGroupRow)[]>(() =>
  buildHeteroRows('standard', 'HeteroStandard', t('compteur.rows.heteroStandard'), compteurStore.orienteDosesHeteroStd, compteurStore.orienteVerratsHeteroStd, compteurStore.orienteDosesHeteroStdAvantLot),
)
const heteroArmadaRows = computed<(HeteroSectionRow | CounterRow | HeteroCondRow | HeteroGroupRow)[]>(() =>
  buildHeteroRows('armada', 'HeteroArmada', t('compteur.rows.heteroArmada'), compteurStore.orienteDosesHeteroArm, compteurStore.orienteVerratsHeteroArm, compteurStore.orienteDosesHeteroArmAvantLot),
)

// ---------------------------------------------------------------------------
// Bloc Parc (U08) — cf. E30-Mapping-Ecran-Compteurs §4.
// ---------------------------------------------------------------------------
// Bandeau de section = habillage (pas de valeur propre), comme Mono/Hétéro.
// Les lignes de groupe (G1..Gn du plan de monte sélectionné) lisent `parcNbVerratByGroupe` (clé externe = creneau
// du plan de monte, ex. "G1" — cf. store).
// "Potentiel Doses C" : figée au RAZ via Baseline.Quantite (même source/moment de calcul que
// "Nb Verrat PDM Commer" ci-dessous) — décision métier confirmée 2026-07-20, cf. wiki §4 note.

/** Détail : lignes Baseline brutes du Parc, avec filtres optionnels créneau/commercial. */
function isBaseline(extra?: (c: ICompteur) => boolean) {
  return (c: ICompteur) => c.nature === 'Baseline' && (extra ? extra(c) : true)
}
const isParcPreleve = (c: ICompteur) => c.nature === 'ParcPreleve'

/** Bâtiment par défaut du site : l'option « bâtiment favori », sinon le premier bâtiment du centre —
 * même règle que le chargement du plan de monte (BuildingComposable). Lecture seule : ne touche pas
 * à `cubicleStore.buildingId`, que le plan de monte fait varier quand on change de bâtiment. */
const defaultBuildingId = computed(() => {
  const centerBuildings = buildingStore.getBuildingsForCurrentCenter
  const favoriteId = Number(optionsStore.getOptionByCodeForCenter(FAVORITE_BUILDING_OPTION)?.value)
  if (favoriteId > 0 && centerBuildings.some((b) => b.id === favoriteId)) return favoriteId
  return centerBuildings[0]?.id ?? 0
})

/** Mantis 0028608 : le bandeau PARC porte le nombre de verrats RÉELLEMENT PLACÉS dans le bâtiment par
 * défaut du site, par schéma — ceux que le plan de monte affiche. Ne comptent pas un verrat sans box,
 * mal placé (emplacement inexistant), en doublon, ni dans un box non autorisé aux verrats (cf.
 * `cubicleStore.getNbPlacedByGeneticType`). Il comptait avant tout le parc du site (arbitrage du
 * 2026-09-10, défait par ce ticket) : les verrats transférés mais pas encore placés gonflaient le
 * nombre de verrats disponibles.
 * Seule ligne du tableau qui ne sort pas de la table `compteurs` : c'est un état courant du
 * bâtiment, pas un compteur daté ; il ignore la date choisie en haut de l'écran. La projeter
 * supposerait un hook sur chaque déplacement de verrat pour une valeur que `cubicle.store` — chargé
 * pour le centre — donne toujours fraîche. Écart assumé au principe D11 « tout dans compteurs ».
 * Les box ne sont pas préchargés au démarrage : `useParcCubicles` les charge s'ils manquent pour ce
 * bâtiment (sans lui, le bandeau restait vide tant qu'on n'était pas passé par le plan de monte).
 * Clé = colonne de BASE du schéma (`${schemaId}____`), comme les autres lignes Parc, qui n'ont ni
 * volume ni concentration. */
const { placedByGeneticType } = useParcCubicles(defaultBuildingId, computed(() => usersStore.currentCenter?.id))
const parcNbVerratPlacesBatiment = computed(() => {
  const byCol = new Map<string, number>()
  for (const [geneticTypeId, count] of placedByGeneticType.value) {
    byCol.set(`${geneticTypeId}____`, count)
  }
  return byCol
})

/** Groupes du plan de monte sélectionné (P.D.M = dernier RAZ du jour) : aucun plan, aucune ligne de groupe. */
const { planGroupNumbers: parcPlanGroupNumbers } = useParcPlanGroups(computed(() => compteurStore.latestRaz?.mountingPlanId))

const parcRows = computed<(ParcSectionRow | ParcRow)[]>(() => {
  const byGroupe = compteurStore.parcNbVerratByGroupe
  const groupRows = parcGroupLabels(parcPlanGroupNumbers.value, byGroupe.keys()).map((groupKey): ParcRow => (
    { _type: 'parcRow', label: groupKey, bold: false, indent: true, byCol: byGroupe.get(groupKey) ?? new Map(), encartId: 'parc', isEncartHeader: false, detailPredicate: isBaseline((c) => c.creneau === groupKey), isDistinctCount: true }
  ))
  return [
    { _type: 'parcSection', label: t('compteur.rows.parc'), byCol: parcNbVerratPlacesBatiment.value, encartId: 'parc', isEncartHeader: true },
    { _type: 'parcRow', label: t('compteur.rows.nbVerratsPDM'),      bold: true,  indent: false, byCol: compteurStore.parcNbVerratPlan,           encartId: 'parc', isEncartHeader: false, detailPredicate: isBaseline(), isDistinctCount: true },
    ...groupRows,
    { _type: 'parcRow', label: t('compteur.rows.nbPrelevements'),    bold: true,  indent: false, byCol: compteurStore.parcNbVerratPreleve,        encartId: 'parc', isEncartHeader: false, detailPredicate: isParcPreleve, isDistinctCount: true },
    { _type: 'parcRow', label: t('compteur.rows.potentielDosesC'),   bold: false, indent: false, byCol: compteurStore.parcPotentielCommercial,     encartId: 'parc', isEncartHeader: false, detailPredicate: isBaseline((c) => c.isCommercial === true) },
    { _type: 'parcRow', label: t('compteur.rows.nbVerratPDMCommer'), bold: false, indent: false, byCol: compteurStore.parcNbVerratCommercial,     encartId: 'parc', isEncartHeader: false, detailPredicate: isBaseline((c) => c.isCommercial === true), isDistinctCount: true },
    { _type: 'parcRow', label: t('compteur.rows.nbVerratPDMNC'),     bold: false, indent: false, byCol: compteurStore.parcNbVerratNonCommercial,  encartId: 'parc', isEncartHeader: false, detailPredicate: isBaseline((c) => c.isCommercial === false), isDistinctCount: true },
  ]
})

// ---------------------------------------------------------------------------
// Bloc Stock — nature=Stock ; segment route Mono/HeteroStandard/HeteroArmada,
// conditionnementId route Selfix/Blister/Vrac (même paramètre quel
// que soit le segment — cf. E30-Mapping-Ecran-Compteurs §5).
// ---------------------------------------------------------------------------

/** Segments affichés en Stock, dans l'ordre d'affichage (préfixe de libellé pour l'hétéro). */
const STOCK_SEGMENTS: { key: string; prefix: string }[] = [
  { key: 'Mono',           prefix: '' },
  { key: 'HeteroStandard', prefix: 'H' },
  { key: 'HeteroArmada',   prefix: 'A' },
]

const stockRows = computed<(StockSectionRow | StockRow)[]>(() => {
  // clé "segment|code3" -> Map<colKey, quantite>
  const byRow = new Map<string, Map<string, number>>()
  const sectionByCol = new Map<string, number>()

  for (const c of datas.value) {
    if (c.nature !== 'Stock' || c.schemaId == null) continue
    const pack = c.conditionnementId != null ? packagingByParamId.value.get(c.conditionnementId) : undefined
    if (!pack) continue

    const rowKey = `${c.segment}|${pack.code3}`
    const colKey = `${c.schemaId}__${c.volume ?? ''}__${c.concentration ?? ''}`

    if (!byRow.has(rowKey)) byRow.set(rowKey, new Map())
    const byCol = byRow.get(rowKey)!
    byCol.set(colKey, (byCol.get(colKey) ?? 0) + c.quantite)
    sectionByCol.set(colKey, (sectionByCol.get(colKey) ?? 0) + c.quantite)
  }

  const packRows: StockRow[] = STOCK_SEGMENTS.flatMap((seg) =>
    PACKAGINGS.value.map((pack) => {
      const condId = packagingIdByCode3.value.get(pack.code3)
      return {
        _type: 'stockRow' as const,
        label: seg.prefix === '' ? pack.label : `${seg.prefix}${pack.code3} - ${pack.label}`,
        byCol: byRow.get(`${seg.key}|${pack.code3}`) ?? new Map(),
        encartId: 'stock',
        isEncartHeader: false,
        detailPredicate: (c: ICompteur) => c.nature === 'Stock' && c.segment === seg.key && c.conditionnementId === condId,
      }
    }),
  )

  return [
    { _type: 'stockSection', label: t('compteur.rows.stock'), byCol: sectionByCol, encartId: 'stock', isEncartHeader: true, detailPredicate: (c: ICompteur) => c.nature === 'Stock' },
    ...packRows,
  ]
})

// Types de ligne dont `byCol` est une Map<string, number> simple (par opposition à CellVal
// {besoin, dispo}) — utilisé par getVal/getTot (Helpers affichage) ET par emptyEncartIds
// ci-dessous ; déclaré ici (avant le watch immediate qui appelle getTot dès le setup) pour
// éviter une TDZ (le const doit être initialisé avant l'exécution du watch immediate).
const NUMBER_MAP_TYPES = new Set(['attente', 'counter', 'parcSection', 'parcRow', 'stockSection', 'stockRow'])

const allRows = computed<AnyRow[]>(() => [
  ...attenteRows.value,
  ...greyRows.value,
  ...heteroStandardRows.value,
  ...heteroArmadaRows.value,
  ...parcRows.value,
  ...stockRows.value,
])

// Blocs sans AUCUNE donnée (TOTAL vide sur tout le bandeau).
// "Parc" est exclu (toujours considéré non-vide : son bandeau n'a structurellement pas de total,
// cf. parcSection, byCol vide en dur). Un encart a des données dès qu'UNE seule de ses lignes
// (bandeau ou détail) a un total non vide (`getTot(row) !== ''`) — couvre le cas Hétéro où les
// lignes "Nbre de doses orientées/Nb verrats orientés" portent des données indépendamment du
// total du bandeau lui-même. Un bloc SANS bandeau (ex. "Attente", jamais isEncartHeader) est aussi
// exclu : replié, il n'aurait plus aucune ligne cliquable pour le rouvrir (cf. toggleEncart).
const emptyEncartIds = computed<Set<string>>(() => {
  const withHeader = new Set<string>()
  const withData = new Set<string>()
  for (const row of allRows.value) {
    if (row.encartId === 'parc') continue
    if (row.isEncartHeader) withHeader.add(row.encartId)
    if (getTot(row) !== '') withData.add(row.encartId)
  }
  const empty = new Set<string>()
  for (const id of withHeader) {
    if (!withData.has(id)) empty.add(id)
  }
  return empty
})

// Repli AUTOMATIQUE (une seule fois par encart) des blocs vides, via le MÊME mécanisme que le
// repli manuel (`collapsedEncarts`) — pas un masquage complet : le bandeau reste visible et
// l'utilisateur peut toujours déplier en cliquant dessus, comme n'importe quel autre bloc.
// `autoCollapseApplied` évite de re-forcer le repli si l'utilisateur l'a rouvert manuellement.
// Ignoré tant que `isLoading` est vrai : au montage, `compteurStore.load()` est asynchrone —
// évaluer `emptyEncartIds` avant sa résolution verrait `datas` encore vide et replierait TOUT
// (Stock inclus, même avec de vraies données) une fois pour toutes.
//
// Le reset (repli manuel ET auto remis à zéro) est fait ICI, dans le MÊME callback, plutôt que
// dans un watch séparé sur `currentDate`/`loadVersion` : deux watchers indépendants s'exécutent
// dans leur ordre de DÉCLARATION dès qu'ils sont déclenchés dans le même flush Vue, quel que soit
// l'ordre réel des mutations réactives qui les ont déclenchés. Pour un changement de jour, le reset
// se produisait dans un flush antérieur (currentDate change AVANT la résolution async de load()),
// donc l'ordre ne posait pas de problème par pur hasard temporel. Mais pour une fin de RAZ/
// changement de plan de monte (`loadVersion` incrémenté DANS load(), au même tick que
// `isLoading=false`), un watch séparé s'exécutant après celui-ci aurait effacé le repli qu'il vient
// tout juste d'appliquer — constat terrain (bloc resté replié malgré des données après RAZ).
const autoCollapseApplied = new Set<string>()
let lastAppliedLoadVersion = -1
watch([emptyEncartIds, isLoading], ([ids, loading]) => {
  if (loading) return
  if (lastAppliedLoadVersion !== loadVersion.value) {
    for (const key of Object.keys(collapsedEncarts)) delete collapsedEncarts[key]
    autoCollapseApplied.clear()
    lastAppliedLoadVersion = loadVersion.value
  }
  for (const id of ids) {
    if (!autoCollapseApplied.has(id)) {
      collapsedEncarts[id] = true
      autoCollapseApplied.add(id)
    }
  }
}, { immediate: true })

const tableData = computed<AnyRow[]>(() => {
  const all = allRows.value

  // `row-key` stable requis par el-table (colonne figée) : sans clé, le nombre de
  // lignes qui change au repli/dépli désynchronise visuellement la colonne figée
  // de la partie défilante. Désambiguïse les labels répétés au sein d'un même
  // encart (ex. G1/G2 par conditionnement hétéro).
  const seen = new Map<string, number>()
  for (const row of all) {
    const base = `${row._type}:${row.encartId}:${row.label}`
    const n = (seen.get(base) ?? 0) + 1
    seen.set(base, n)
    row.rowKey = n === 1 ? base : `${base}:${n}`
  }

  // Un encart replié retire ses lignes de détail (disparition nette) ; sa ligne
  // bandeau/en-tête reste visible. Le fondu de sortie se joue AVANT ce retrait
  // (cf. toggleEncart/fadingEncarts) — une fois ici, la ligne disparaît d'un coup.
  return all.filter((row) => row.isEncartHeader || !collapsedEncarts[row.encartId])
})

// ---------------------------------------------------------------------------
// Helpers affichage
// ---------------------------------------------------------------------------

/** Lignes "Gx réservé" : toujours affichées même à 0, quel que soit `showZeroValues`
 * (seule exception à l'option "Afficher les valeurs à 0"). */
function isGxReservedRow(row: AnyRow): boolean {
  return row._type === 'group' && row.isReserved === true
}

/** COUNT(DISTINCT boarId) sur les lignes brutes matchant `predicate` (et, si fourni, dont
 * `colKeyOf` est dans `matchKeys`) — recalcul direct depuis `datas.value`, jamais une somme de
 * comptes déjà agrégés par colonne (cf. AttenteRow.isDistinctCount). */
function distinctBoarCount(predicate: (c: ICompteur) => boolean, matchKeys?: string[]): number | '' {
  const keys = matchKeys ? new Set(matchKeys) : null
  const boars = new Set<number>()
  for (const c of datas.value) {
    if (!predicate(c)) continue
    if (keys && !keys.has(colKeyOf(c))) continue
    if (c.boarId != null) boars.add(c.boarId)
  }
  return boars.size > 0 ? boars.size : ''
}

function getVal(row: AnyRow, col: ColumnDef): number | '' {
  // `col.matchKeys` : 1 clé pour une colonne configurée (== col.key), plusieurs pour
  // une colonne "simple" de schéma hors config (agrège toutes les variantes volume/
  // concentration trouvées dans les données sous une colonne unique, ex. "THO").
  if (NUMBER_MAP_TYPES.has(row._type)) {
    const r = row as AttenteRow | CounterRow | ParcSectionRow | ParcRow | StockSectionRow | StockRow
    if (r.isDistinctCount && r.detailPredicate) return distinctBoarCount(r.detailPredicate, col.matchKeys)
    const byCol = r.byCol
    let found = false
    let sum = 0
    for (const k of col.matchKeys) {
      const v = byCol.get(k)
      if (v !== undefined) { found = true; sum += v }
    }
    if (!found) return ''
    if (sum === 0 && !showZeroValues.value) return ''
    return sum
  }
  // `found` (cellule réellement alimentée, même si le net vaut 0 — ex. Besoin intégralement
  // couvert par du Dispo du même verrat) est distingué de "pas de donnée du tout" : sinon un
  // "0" qui prouve qu'une commande a été couverte disparaîtrait, indiscernable d'une case vide.
  const byCol = (row as CondRow | GroupRow | HeteroSectionRow | HeteroCondRow | HeteroGroupRow).byCol
  let found = false
  let besoin = 0
  let dispo = 0
  for (const k of col.matchKeys) {
    const cell = byCol.get(k)
    if (cell) { found = true; besoin += cell.besoin; dispo += cell.dispo }
  }
  if (!found) return ''
  const net = dispo - besoin
  if (net === 0 && !showZeroValues.value && !isGxReservedRow(row)) return ''
  return net
}

function getTot(row: AnyRow): number | '' {
  // Le TOTAL est le total des LIGNES AFFICHÉES (arbitrage métier 2026-08-17) : une donnée dont
  // la colonne a été écartée par REQ887 (schéma ni sélectionné, ni commandé, ni en stock, ni au
  // plan de monte — cf. canOpenColumn) n'y entre pas, sinon le total afficherait une quantité
  // qu'aucune colonne ne porte.
  const shownKeys = displayedMatchKeys.value
  if (NUMBER_MAP_TYPES.has(row._type)) {
    const r = row as AttenteRow | CounterRow | ParcSectionRow | ParcRow | StockSectionRow | StockRow
    // Jamais sommer des COUNT(DISTINCT boarId) déjà agrégés par colonne : un même verrat présent
    // sur 2 colonnes serait compté 2 fois côté total (constat terrain 2026-07-28, "Nb. Ver.diff."
    // affichant 8 alors que le détail n'en listait que 5) — on recompte sur les lignes brutes
    // matchées par le prédicat, dédoublonnées, en se limitant aux colonnes affichées.
    if (r.isDistinctCount && r.detailPredicate) return distinctBoarCount(r.detailPredicate, [...shownKeys])
    let found = false
    let s = 0
    for (const [k, v] of r.byCol) {
      if (!shownKeys.has(k)) continue
      found = true
      s += v
    }
    if (!found) return ''
    if (s === 0 && !showZeroValues.value) return ''
    return s
  }
  // Même principe que getVal : une cellule alimentée (même nette à 0) reste visible.
  let found = false
  let net = 0
  for (const [k, c] of (row as CondRow | GroupRow | HeteroSectionRow | HeteroCondRow | HeteroGroupRow).byCol) {
    if (!shownKeys.has(k)) continue
    found = true
    net += c.dispo - c.besoin
  }
  if (!found) return ''
  if (net === 0 && !showZeroValues.value && !isGxReservedRow(row)) return ''
  return net
}

function isNeg(v: number | ''): boolean { return typeof v === 'number' && v < 0 }

// ---------------------------------------------------------------------------
// Détail de cellule (clic) : décomposition d'une valeur agrégée (ex. "Selfix 9") en
// lignes brutes (par verrat), ou en son couple Besoin(commande)/Dispo(dispatch) pour les
// cellules "nettes" (bloc gris Mono/Hétéro). Aucun nouvel appel réseau : tout est dérivé de
// `compteurStore.datas` déjà chargé, sauf la résolution du nom des verrats absents du store
// actif (réformés/autre centre), même pattern que ReservedBoarsBadge.vue.
// ---------------------------------------------------------------------------

/** Réplique compteur.store.ts:colKey — Baseline/ParcPreleve n'ont jamais volume/concentration
 * renseignés, donc cette même formule retombe naturellement sur la clé "de base" pour eux. */
function colKeyOf(c: ICompteur): string {
  return `${c.schemaId ?? ''}__${c.volume ?? ''}__${c.concentration ?? ''}`
}

/** Types dont la cellule est un couple {besoin, dispo} (valeur nette affichée = dispo - besoin). */
const CELLVAL_TYPES = new Set(['cond', 'group', 'heteroSection', 'heteroCond', 'heteroGroup'])

const devOptionsStore = useDevOptionsStore()
const detailVisible = ref(false)
const detailAnchorEl = ref<HTMLElement | null>(null)
const detailRow = ref<AnyRow | null>(null)
const detailCol = ref<ColumnDef | null>(null)

function onCellClick(event: MouseEvent, row: AnyRow, col: ColumnDef): void {
  if (!hasDetail(row, col)) return
  detailAnchorEl.value = event.currentTarget as HTMLElement
  detailRow.value = row
  detailCol.value = col
  detailVisible.value = true
}

/** Une cellule n'offre un détail que si elle porte une donnée ET (c'est une cellule
 * Besoin/Dispo du bloc gris, ou sa ligne porte un `detailPredicate` par verrat) ET que la
 * bascule "Outils de développement" est activée (désactivée par défaut, cf. Menu.vue). */
function hasDetail(row: AnyRow, col: ColumnDef): boolean {
  if (!devOptionsStore.showCompteurCellDetail) return false
  if (getVal(row, col) === '') return false
  if (CELLVAL_TYPES.has(row._type)) return true
  return typeof (row as { detailPredicate?: unknown }).detailPredicate === 'function'
}

interface DetailBoarLine { boarId: number | null; boarName: string; quantite: number }

/** Lignes brutes correspondant à (row, col) via `row.detailPredicate`, groupées par verrat. */
const detailBoarLines = computed<DetailBoarLine[]>(() => {
  const row = detailRow.value as (AttenteRow | CounterRow | ParcRow | StockSectionRow | StockRow) | null
  const col = detailCol.value
  const predicate = row?.detailPredicate
  if (!row || !col || !predicate) return []

  const matchKeys = new Set(col.matchKeys)
  const byBoar = new Map<number | null, number>()
  for (const c of datas.value) {
    if (!predicate(c) || !matchKeys.has(colKeyOf(c))) continue
    const key = c.boarId ?? null
    byBoar.set(key, (byBoar.get(key) ?? 0) + c.quantite)
  }

  return [...byBoar.entries()]
    .map(([boarId, quantite]) => ({ boarId, quantite, boarName: boarId != null ? boarName(boarId) : t('compteur.detail.unknownBoar') }))
    .sort((a, b) => a.boarName.localeCompare(b.boarName))
})

/** Besoin (commande) / Dispo (dispatch) déjà calculés par le moteur de rapprochement pour cette
 * cellule (bloc gris Mono/Hétéro) — pas de nouveau calcul, juste la réexposition du CellVal. */
const detailCellVal = computed<CellVal | null>(() => {
  const row = detailRow.value
  const col = detailCol.value
  if (!row || !col || !CELLVAL_TYPES.has(row._type)) return null
  const byCol = (row as CondRow | GroupRow | HeteroSectionRow | HeteroCondRow | HeteroGroupRow).byCol
  const total = emptyCell()
  for (const k of col.matchKeys) {
    const cell = byCol.get(k)
    if (cell) { total.besoin += cell.besoin; total.dispo += cell.dispo }
  }
  return total
})

const detailTitle = computed(() => {
  if (!detailRow.value || !detailCol.value) return ''
  return `${detailRow.value.label} — ${detailCol.value.label}`
})

// Résolution du nom des verrats absents du store actif (réformés ou mutés vers un autre centre)
// — même pattern que ReservedBoarsBadge.vue : fallback réformés du centre puis fetch direct.
const extraBoarNames = ref<Map<number, string>>(new Map())

function boarName(boarId: number): string {
  const boar = boarsStore.getByIdInStore(boarId)
  if (boar) return boar.name ?? `#${boarId}`
  return extraBoarNames.value.get(boarId) ?? `#${boarId}`
}

async function resolveMissingBoarNames(boarIds: (number | null)[]): Promise<void> {
  const missing = [...new Set(boarIds)].filter(
    (id): id is number => id != null && !boarsStore.getByIdInStore(id) && !extraBoarNames.value.has(id),
  )
  if (missing.length === 0) return

  await boarsStore.loadReformed()
  const stillMissing = missing.filter((id) => !boarsStore.getByIdInStore(id))
  if (stillMissing.length === 0) return

  const fetched = await Promise.all(stillMissing.map((id) => AnimalService.getBoarById(id)))
  const valid = fetched.filter((b): b is NonNullable<typeof b> => b != null)
  if (valid.length === 0) return

  const hydrated = await boarsStore.hydrateBoars(valid)
  const next = new Map(extraBoarNames.value)
  for (const boar of hydrated) next.set(boar.id, boar.name ?? `#${boar.id}`)
  extraBoarNames.value = next
}

watch(detailBoarLines, (lines) => { resolveMissingBoarNames(lines.map((l) => l.boarId)) })

function baseRowClass(row: AnyRow): string {
  switch (row._type) {
    case 'attente':        return 'row-attente'
    case 'cond':           return 'row-cond'
    case 'group':          return row.isReserved ? 'row-group-reserved' : 'row-group'
    case 'counter':        return row.variant === 'standard' ? 'row-counter-standard' : 'row-counter-armada'
    case 'heteroSection':  return row.variant === 'standard' ? 'row-hetero-section-standard' : 'row-hetero-section-armada'
    case 'heteroCond':     return row.variant === 'standard' ? 'row-hetero-cond-standard' : 'row-hetero-cond-armada'
    case 'heteroGroup':    return row.variant === 'standard' ? 'row-hetero-group-standard' : 'row-hetero-group-armada'
    case 'parcSection':    return 'row-parc-section'
    case 'parcRow':        return 'row-parc'
    case 'stockSection':   return 'row-stock-section'
    case 'stockRow':       return 'row-stock'
  }
}

/** REQ904 : lignes qui restent visibles mais atténuées en mode anticipation (Stock + compteurs
 * "orienté" Hétéro) — ignorées par le rapprochement Besoin/offre, mais pas masquées pour autant. */
const ANTICIPATION_DIMMED_TYPES = new Set(['stockSection', 'stockRow', 'counter'])

function rowClass({ row }: { row: AnyRow }): string {
  const base = baseRowClass(row)
  const classes = [base]
  // Fondu de sortie (juste avant le retrait réel de la ligne, cf. toggleEncart).
  if (!row.isEncartHeader && fadingEncarts[row.encartId]) classes.push('row-fading')
  if (anticipationMode.value && ANTICIPATION_DIMMED_TYPES.has(row._type)) classes.push('row-dimmed')
  return classes.join(' ')
}

// ---------------------------------------------------------------------------
// Descriptions (tooltip "i" sur la colonne libellé) — DÉSACTIVÉ (pollue l'espace).
// Conservé en commentaire pour réactivation éventuelle.
// ---------------------------------------------------------------------------

// /** Descriptions à texte fixe, indexées par "_type:label". */
// const ROW_DESCRIPTIONS: Record<string, string> = {
//   'attente:Dispo mono (attente)':       "Nombre de doses mono-race disponibles en attente d'affectation à un conditionnement.",
//   'attente:Nb. Ver.diff.':              'Nombre de verrats différents ayant contribué aux doses disponibles.',
//   'attente:Dispo Hétéro Std (attente)': "Nombre de doses hétérozygotes Standard disponibles en attente d'affectation.",
//   'attente:Dispo Hétéro Arm (attente)': "Nombre de doses hétérozygotes Armada disponibles en attente d'affectation.",
//   'heteroSection:Hétéro Standard':     'Bloc de suivi des doses hétérozygotes de variante Standard.',
//   'heteroSection:Hétéro Armada':       'Bloc de suivi des doses hétérozygotes de variante Armada.',
//   'counter:Nbre de doses orientées':   'Nombre de doses orientées vers cette variante hétérozygote.',
//   'counter:Nb verrats orientés':       'Nombre de verrats orientés vers cette variante hétérozygote.',
//   'parcSection:Parc':                  'Suivi des effectifs et prélèvements du parc de verrats.',
//   'parcRow:Nb Verrats PDM':            'Nombre de verrats en prélèvement de mise en dose (PDM).',
//   'parcRow:Nb Prélèvements':           'Nombre total de prélèvements réalisés sur le parc.',
//   'parcRow:Potentiel Doses C':         "Somme des potentiels de doses commercialisables, figée au dernier RAZ (mise à jour au choix du plan de monte).",
//   'parcRow:Nb Verrat PDM Commer':      'Nombre de verrats PDM dont la production est commercialisable.',
//   'parcRow:Nb Verrat PDM NC':          'Nombre de verrats PDM non conformes (NC).',
//   'stockSection:Stock':                'Récapitulatif du stock de doses par conditionnement, toutes variantes confondues (mono, hétéro Standard, hétéro Armada).',
// }
//
// /** Description d'une ligne de conditionnement/groupe, calculée dynamiquement à partir du contexte. */
// function getDescription(row: AnyRow): string {
//   const fixed = ROW_DESCRIPTIONS[`${row._type}:${row.label}`]
//   if (fixed) return fixed
//
//   switch (row._type) {
//     case 'cond':
//       return `Récapitulatif du conditionnement ${row.label} : besoin et disponible cumulés sur tous les créneaux.`
//     case 'group':
//       return row.isReserved
//         ? `Créneau ${row.label.replace(' réservé', '')} : doses réservées, non disponibles à l'affectation standard.`
//         : `Créneau ${row.label} : doses disponibles à l'affectation standard.`
//     case 'heteroCond':
//       return `Conditionnement ${row.label} pour les doses hétérozygotes.`
//     case 'heteroGroup':
//       return `Créneau ${row.label} du conditionnement hétérozygote.`
//     case 'parcRow':
//       return row.indent
//         ? `Répartition par groupe (${row.label}) de la ligne "Nb Verrats PDM" ci-dessus.`
//         : ''
//     case 'stockRow':
//       return `Stock de doses disponible en conditionnement ${row.label}.`
//     default:
//       return ''
//   }
// }

// ---------------------------------------------------------------------------
// Flash SignalR
// ---------------------------------------------------------------------------

const flashActive = ref(false)
let flashTimer: ReturnType<typeof setTimeout> | null = null

watch(version, () => {
  flashActive.value = true
  if (flashTimer) clearTimeout(flashTimer)
  flashTimer = setTimeout(() => { flashActive.value = false }, 700)
})

onUnmounted(() => { if (flashTimer) clearTimeout(flashTimer) })
</script>

<template>
  <div class="compteurs-view" v-loading="isLoading" :element-loading-text="loadingMessage">

    <!-- RAZ (U03) : panneau de progression par étape (Besoin/Dispo/Attente/Parc/Stock/Baseline),
         recalculées en parallèle côté serveur — remplace un spinner générique bloquant ~5 min sans
         aucun retour visible (cf. analyse RAZ 2026-07-28). -->
    <LoadingSteps :show="razInProgress" :steps="razLoadingSteps" active-icon="spinner" />

    <!-- Navigation date -->
    <div class="date-nav">
      <el-button type="primary" size="small" @click="prevDay">&#8249;</el-button>
      <span class="date-label" :class="{ 'date-label--today': isToday }">{{ getDateLongFrenchFormat(currentDate) }}</span>
      <el-button type="primary" size="small" @click="nextDay">&#8250;</el-button>
      <ReservedBoarsBadge />
      <PlanDeMonteBadge :current-date="currentDate" />

      <!-- REQ904 : mode anticipation (ignore Dispo/Stock, ne montre que commandes/plan de monte).
           Le switch expose l'inverse (« Prise en compte du stock »), cf. stockTakenIntoAccount. -->
      <div class="anticipation-toggle" :title="$t('compteur.toggle.stockTooltip')">
        <span class="anticipation-toggle__label">{{ $t('compteur.toggle.stockLabel') }}</span>
        <el-switch
          v-model="stockTakenIntoAccount"
          inline-prompt
          active-text=""
          inactive-text=""
          class="anticipation-toggle__switch"
        />
      </div>

      <!-- Masque les cellules à 0 (sauf lignes "Gx réservé", toujours affichées). -->
      <div class="zero-values-toggle" :title="$t('compteur.toggle.zeroValuesTooltip')">
        <span class="zero-values-toggle__label">{{ $t('compteur.toggle.zeroValuesLabel') }}</span>
        <el-switch
          v-model="showZeroValues"
          inline-prompt
          active-text=""
          inactive-text=""
          class="zero-values-toggle__switch"
        />
      </div>
    </div>

    <!-- Tableau -->
    <div :class="['table-wrapper', { flash: flashActive }]">
      <el-table
        :key="columnsSignature"
        :data="tableData"
        :row-class-name="rowClass"
        :row-key="(row: AnyRow) => row.rowKey"
        :header-cell-style="{ background: '#2B48A2', color: '#ffffff' }"
        height="100%"
        border
        size="small"
        :empty-text="$t('compteur.table.emptyText')"
      >
        <!-- Col 1 : libellé (figée pendant le scroll horizontal) -->
        <el-table-column label="" width="200" align="left" fixed="left" class-name="col-label">
          <template #default="{ row }">
            <span
              v-if="row.isEncartHeader"
              class="lbl-toggle"
              :title="collapsedEncarts[row.encartId] ? $t('compteur.table.expand') : $t('compteur.table.collapse')"
              @click="toggleEncart(row.encartId)"
            >{{ collapsedEncarts[row.encartId] ? '▸' : '▾' }}</span>
            <span :class="{
              'lbl-cond':   row._type === 'cond' || row._type === 'heteroSection' || row._type === 'heteroCond' || row._type === 'parcSection' || row._type === 'stockSection',
              'lbl-attente': row._type === 'attente',
              'lbl-bold':   row._type === 'parcRow' && row.bold,
              'lbl-indent': row._type === 'parcRow' && row.indent,
              'lbl-clickable': row.isEncartHeader,
            }" @click="row.isEncartHeader && toggleEncart(row.encartId)">{{ row.label }}</span>
            <!-- Tooltip "i" descriptif désactivé (pollue l'espace) — cf. ROW_DESCRIPTIONS/getDescription dans le script.
            <el-tooltip v-if="getDescription(row)" :content="getDescription(row)" placement="top" effect="blue" raw-content>
              <el-icon class="lbl-info"><InfoFilled color="#3498db" /></el-icon>
            </el-tooltip>
            -->
          </template>
        </el-table-column>

        <!-- TOTAL (figée avec la colonne libellé pendant le scroll horizontal) -->
        <el-table-column width="55" align="right" fixed="left">
          <template #header>
            <span class="col-schema-label">{{ $t('compteur.table.total') }}</span>
          </template>
          <template #default="{ row }">
            <span :class="{
              'val-neg':    isNeg(getTot(row)),
              'lbl-cond':   row._type === 'cond' || row._type === 'heteroSection' || row._type === 'heteroCond' || row._type === 'parcSection' || row._type === 'stockSection',
              'lbl-bold':   row._type === 'parcRow' && row.bold,
            }">
              {{ getTot(row) }}
            </span>
          </template>
        </el-table-column>

        <!-- Colonnes dynamiques (codes schéma) -->
        <el-table-column
          v-for="col in columns"
          :key="col.key"
          width="58"
          align="right"
          header-align="center"
          :label-class-name="col.isUnconfigured ? 'col-unconfigured-header' : (col.volume != null || col.concentration != null) ? 'col-specific-header' : undefined"
        >
          <template #header>
            <span class="col-schema-label">{{ col.label }}</span>
          </template>
          <template #default="{ row }">
            <span :class="{
              'val-neg':    isNeg(getVal(row, col)),
              'lbl-cond':   row._type === 'cond' || row._type === 'heteroSection' || row._type === 'heteroCond' || row._type === 'parcSection' || row._type === 'stockSection',
              'lbl-bold':   row._type === 'parcRow' && row.bold,
              'cell-detail': hasDetail(row, col),
            }"
              @click="onCellClick($event, row, col)"
            >
              {{ getVal(row, col) }}
            </span>
          </template>
        </el-table-column>

      </el-table>

      <!-- Détail d'une cellule (clic) : Besoin/Dispo pour le bloc gris Mono/Hétéro, sinon
           décomposition par verrat — cf. cellDetail/detailBoarLines. -->
      <el-popover
        v-model:visible="detailVisible"
        :virtual-ref="detailAnchorEl ?? undefined"
        virtual-triggering
        placement="top"
        width="280"
        popper-class="cell-detail-popover"
      >
        <div class="cell-detail__title">{{ detailTitle }}</div>

        <div v-if="detailCellVal" class="cell-detail__cellval">
          <div class="cell-detail__line"><span>{{ $t('compteur.detail.besoin') }}</span><strong>{{ detailCellVal.besoin }}</strong></div>
          <div class="cell-detail__line"><span>{{ $t('compteur.detail.dispo') }}</span><strong>{{ detailCellVal.dispo }}</strong></div>
          <div class="cell-detail__line cell-detail__line--total"><span>{{ $t('compteur.detail.solde') }}</span><strong>{{ detailCellVal.dispo - detailCellVal.besoin }}</strong></div>
        </div>

        <el-table v-else :data="detailBoarLines" size="small" max-height="260" border :empty-text="$t('compteur.detail.emptyText')">
          <el-table-column :label="$t('compteur.detail.boar')">
            <template #default="{ row }">{{ row.boarName }}</template>
          </el-table-column>
          <el-table-column :label="$t('compteur.detail.quantity')" width="80" align="right">
            <template #default="{ row }">{{ row.quantite }}</template>
          </el-table-column>
        </el-table>
      </el-popover>
    </div>
  </div>
</template>

<style scoped>
.compteurs-view {
  padding: 8px 12px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  font-family: "OpenSans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Design repris de feature/ft-poc-compteur : bordures bleu officiel du site
   plutôt que le gris/noir par défaut d'el-table, typo compacte. */
:deep(.el-table) {
  font-family: "OpenSans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  --el-table-border-color: #2B48A2;
  --el-table-header-border-color: #ffffff;
  border: 1px solid #2B48A2;
}

/* Séparateurs de colonnes en blanc dans l'en-tête (fond bleu) : en bleu, elles
   étaient invisibles sur fond bleu. */
:deep(.el-table th) {
  font-size: 11px;
  font-weight: 700;
  border-color: #ffffff;
}

/* En-tête des colonnes dynamiques (codes schéma, ex. "HMX") : plus grand que
   les autres en-têtes pour rester lisible en un coup d'œil. */
.col-schema-label {
  font-size: 13px;
  font-weight: 700;
}

/* Lignes compactes (page pleine hauteur : on privilégie le nombre de lignes visibles
   sans scroll, cf. maquette OGICOM legacy) — remplace le line-height/padding par
   défaut d'el-table (23px + 4px), trop généreux une fois la page entière disponible.
   `!important` nécessaire : la classe `.el-table--small .el-table__cell` (2 classes)
   est plus spécifique qu'un simple `.el-table td`/`th`. */
:deep(.el-table .el-table__cell) {
  padding: 2px 0 !important;
}
:deep(.el-table .cell) {
  line-height: 18px;
}

/* Colonne d'un schéma hors préférences configurées (ex. THO) : toujours affichée
   (une activité réelle ne doit jamais être masquée) et repoussée en fin de tableau
   par le tri de `columns`. Seul l'en-tête est mis en évidence (ambre) pour signaler
   qu'elle sort de la config du centre ; les cellules de données restent dans les
   couleurs normales du bloc (Attente/Mono/Hétéro/Parc/Stock). `!important` gagne
   sur le `header-cell-style` inline (bleu, posé sur toutes les colonnes). */
:deep(.el-table th.col-unconfigured-header) {
  background-color: #b45309 !important;
  color: #ffffff !important;
}

/* Colonne "spécifique" (volume et/ou concentration précisés, ex. "HMX 40 [27]") : en-tête
   distingué des colonnes génériques par schéma (ex. "HMX" seul), pour repérer en un coup
   d'œil qu'une commande a demandé un volume/concentration précis plutôt que le générique. */
:deep(.el-table th.col-specific-header) {
  background-color: #9b59b6 !important;
  color: #ffffff !important;
}

/* Colonne libellé (1ère colonne) : texte un peu plus grand que le reste du tableau.
   `white-space: nowrap` + l'ellipsis déjà posé par el-table (.cell) garantit un
   libellé sur une seule ligne quelle que soit sa longueur, plutôt que de retomber
   sur le `white-space: normal` par défaut d'el-table qui le ferait passer à la ligne. */
:deep(.el-table td.col-label .cell) {
  font-size: 12px;
  white-space: nowrap;
}

:deep(.el-table td) {
  border-color: #2B48A2;
}

/* Valeurs du tableau (hors libellé, qui a sa propre taille ci-dessus) : plus grandes que
   la base, sans toucher à l'en-tête. */
:deep(.el-table td:not(.col-label)) {
  font-size: 15px;
}

:deep(.el-table th .cell),
:deep(.el-table td .cell) {
  padding: 0 5px;
}

/* Base transparente : toutes les lignes ont la même largeur de bordure gauche/droite,
   qu'elles appartiennent ou non à un bloc coloré (évite un décalage visuel du texte). */
:deep(.el-table td) {
  border-left: 1px solid transparent;
  border-right: 1px solid transparent;
}

/* Colonne figée (libellé) : fonds pleins, pas de rgba — la transparence y laisse
   voir le contenu défiler en dessous, effet "moche" au scroll horizontal. Couleurs
   = équivalent opaque des rgba(...) utilisés ailleurs (mêmes teintes sur fond blanc). */
:deep(.el-table__fixed .row-counter-standard td)       { background-color: #e8c6eb !important; }
:deep(.el-table__fixed .row-counter-armada td)         { background-color: #c7d3f5 !important; }
:deep(.el-table__fixed .row-hetero-group-standard td)  { background-color: #e8c6eb !important; }
:deep(.el-table__fixed .row-hetero-group-armada td)    { background-color: #c7d3f5 !important; }
:deep(.el-table__fixed .row-parc td)                   { background-color: #c5dfcf !important; }
:deep(.el-table__fixed .row-stock td)                  { background-color: #f0d0c2 !important; }

/* Scrollbars visibles, couleurs du site (cf. @mixin scrollbarBlack).
   El-table ne rend pas de scrollbar native (body-wrapper est en overflow:hidden) :
   il délègue le scroll à son composant interne el-scrollbar, qui dessine son propre
   thumb — donc on surcharge ses variables CSS plutôt que ::-webkit-scrollbar.
   Ces variables doivent être posées sur .el-scrollbar lui-même : le composant les
   redéclare en local (valeurs par défaut du thème), donc les poser seulement sur
   un ancêtre comme .el-table ne les propage pas jusqu'au thumb. */
:deep(.el-table__body-wrapper .el-scrollbar) {
  --el-scrollbar-bg-color: #4c516b;
  --el-scrollbar-hover-bg-color: #333646;
  --el-scrollbar-opacity: 1;
  --el-scrollbar-hover-opacity: 1;
}

:deep(.el-table__body-wrapper .el-scrollbar__bar.is-vertical) {
  width: 6px;
}
:deep(.el-table__body-wrapper .el-scrollbar__bar.is-horizontal) {
  height: 6px;
}

/* La colonne figée (libellé) affiche une ombre portée quand on scrolle
   horizontalement — moche mélangée à nos fonds de ligne saturés, on la retire. */
:deep(.el-table__fixed) {
  box-shadow: none !important;
}

.date-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.date-label {
  font-weight: 600;
  font-size: 0.95em;
  text-transform: capitalize;
}

.date-label--today {
  color: #2B48A2;
}

/* Toggle "Anticipation" (REQ904) : même famille visuelle que les badges ronds voisins
   (plan-de-monte-trigger/reserved-boars-trigger) — pastille bordée bleu, fond blanc. */
.anticipation-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  background: #ffffff;
  color: #2b48a2;
  border: 1px solid #2b48a2;
  border-radius: 16px;
}

.anticipation-toggle__label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.anticipation-toggle__switch {
  --el-switch-on-color: #2b48a2;
  --el-switch-off-color: #c7ccda;
}

.zero-values-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  background: #ffffff;
  color: #2b48a2;
  border: 1px solid #2b48a2;
  border-radius: 16px;
}

.zero-values-toggle__label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.zero-values-toggle__switch {
  --el-switch-on-color: #2b48a2;
  --el-switch-off-color: #c7ccda;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  transition: box-shadow 0.4s ease;
}

.table-wrapper.flash {
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.4);
  border-radius: 4px;
}

/* Labels */
.lbl-attente {
  font-weight: 700;
}
.lbl-cond   { font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.lbl-bold   { font-weight: 700; }
.lbl-indent { padding-left: 16px; }
.lbl-info   { margin-left: 4px; vertical-align: -1px; cursor: help; font-size: 13px; }

/* Repli/dépli d'un encart (ligne bandeau/en-tête) */
.lbl-toggle {
  display: inline-block;
  width: 12px;
  margin-right: 2px;
  cursor: pointer;
  user-select: none;
}
.lbl-clickable { cursor: pointer; }

/* Valeurs */
.val-neg {
  color: #dc2626;
  font-weight: 600;
}

/* ── Couleurs de lignes : chaque bloc forme un "encart" ──────────────────────
   Un filet fin (1px) gauche/droite sur tout le bloc (+ haut sur le bandeau
   quand il y en a un), plutôt qu'un gros trait sur chaque ligne. ── */

/* Encart Attente (habillage jaune, fidèle aux specs Excel) */
:deep(.row-attente td) {
  background-color: var(--o2s-amber-bg) !important;
  border-left: 1px solid #2B48A2 !important;
  border-right: 1px solid #2B48A2 !important;
}
:deep(.row-attente td .cell) {
  color: var(--o2s-amber-text);
}

/* Encart Mono (gris) — chaque conditionnement (Selfix, Blister...) est directement
   son propre bloc, pas de bandeau de section au-dessus (cf. maquette). */
:deep(.row-cond td) {
  background-color: #95a5a6 !important;
  border-top: 1px solid #95a5a6 !important;
}
:deep(.row-cond td .cell) {
  color: #ffffff;
}
:deep(.row-cond td),
:deep(.row-group-reserved td),
:deep(.row-group td) {
  border-left: 1px solid #95a5a6 !important;
  border-right: 1px solid #95a5a6 !important;
}

/* Sous-lignes Gx / HG : fond blanc (pas de teinte), comme les sous-lignes des autres
   blocs (Hétéro...) — seul le bandeau/en-tête garde une couleur pleine. */

/* REQ918 (U05) : lignes "Gx réservé" surlignées en jaune — même palette que l'encart
   Attente — pour alerter l'opérateur que des verrats font partie d'une commande réservée. */
:deep(.row-group-reserved td) {
  background-color: var(--o2s-amber-bg) !important;
}
:deep(.row-group-reserved td .cell) {
  color: var(--o2s-amber-text);
  font-weight: 600;
}

/* Compteurs sous le bandeau Hétéro Standard (teinte magenta) 
:deep(.row-counter-standard td) {
  background-color: rgba(162, 28, 175, 0.25) !important;
}
*/

:deep(.row-counter-standard td) {
  border-left: 1px solid #a21caf !important;
  border-right: 1px solid #a21caf !important;
}

/* Compteurs sous le bandeau Hétéro Armada (teinte bleu) 
:deep(.row-counter-armada td) {
  background-color: rgba(29, 78, 216, 0.25) !important;
}
*/
  
:deep(.row-counter-armada td) {
  border-left: 1px solid #1d4ed8 !important;
  border-right: 1px solid #1d4ed8 !important;
}

/* Encart Hétéro Standard (magenta) */
:deep(.row-hetero-section-standard td) {
  background-color: #a21caf !important;
  border-top: 1px solid #a21caf !important;
}
:deep(.row-hetero-section-standard td .cell) {
  color: #ffffff;
}
:deep(.row-hetero-section-standard td),
:deep(.row-hetero-cond-standard td),
:deep(.row-hetero-group-standard td) {
  border-left: 1px solid #a21caf !important;
  border-right: 1px solid #a21caf !important;
}

/* En-tête conditionnement Hétéro Standard (rose clair) */
:deep(.row-hetero-cond-standard td) {
  background-color: #fbcfe8 !important;
}


:deep(.row-hetero-cond-standard td .cell) {
  color: #111827;
}

/* Sous-lignes Gx Hétéro Standard (teinte magenta) 
:deep(.row-hetero-group-standard td) {
  background-color: rgba(162, 28, 175, 0.25) !important;
}
*/

/* Encart Hétéro Armada (bleu) */
:deep(.row-hetero-section-armada td) {
  background-color: #1d4ed8 !important;
  border-top: 1px solid #1d4ed8 !important;
}
:deep(.row-hetero-section-armada td .cell) {
  color: #ffffff;
}
:deep(.row-hetero-section-armada td),
:deep(.row-hetero-cond-armada td),
:deep(.row-hetero-group-armada td) {
  border-left: 1px solid #1d4ed8 !important;
  border-right: 1px solid #1d4ed8 !important;
}

/* En-tête conditionnement Hétéro Armada (bleu clair) */
:deep(.row-hetero-cond-armada td) {
  background-color: #dbeafe !important;
}
:deep(.row-hetero-cond-armada td .cell) {
  color: #111827;
}

/* Sous-lignes Gx Hétéro Armada (teinte bleu) 
:deep(.row-hetero-group-armada td) {
  background-color: rgba(29, 78, 216, 0.25) !important;
}
*/

/* Encart Parc (vert) */
:deep(.row-parc-section td) {
  background-color: #15803d !important;
  border-top: 1px solid #15803d !important;
}
:deep(.row-parc-section td .cell) {
  color: #ffffff;
}
:deep(.row-parc-section td),
:deep(.row-parc td) {
  border-left: 1px solid #15803d !important;
  border-right: 1px solid #15803d !important;
}

/* Lignes du bloc Parc (teinte verte) 
:deep(.row-parc td) {
  background-color: rgba(21, 128, 61, 0.25) !important;
}
*/  

/* Encart Stock (orange) */
:deep(.row-stock-section td) {
  background-color: #c2410c !important;
  border-top: 1px solid #c2410c !important;
}
:deep(.row-stock-section td .cell) {
  color: #ffffff;
}
:deep(.row-stock-section td),
:deep(.row-stock td) {
  border-left: 1px solid #c2410c !important;
}
:deep(.row-stock-section td),
:deep(.row-stock td) {
  border-right: 1px solid #c2410c !important;
}

/* Lignes du bloc Stock (teinte orange)
:deep(.row-stock td) {
  background-color: rgba(194, 65, 12, 0.25) !important;
}
*/

/* REQ904 : Stock + compteurs "orienté" atténués (pas masqués) en mode anticipation — signale
   que cette donnée reste visible mais n'est plus prise en compte dans le rapprochement affiché. */
:deep(.row-dimmed td) {
  opacity: 0.4;
  filter: grayscale(60%);
}

/* ── Animation de repli d'un encart ────────────────────────────────────────
   La ligne est réellement retirée de tableData (disparition nette, pas de
   ligne "rétrécie" qui reste affichée) ; row-fading ne sert qu'à jouer un
   court fondu JUSTE AVANT ce retrait (cf. toggleEncart). */
:deep(.el-table td) {
  transition: opacity 0.15s ease;
}
:deep(.row-fading td) {
  opacity: 0;
}

/* Détail de cellule (clic) : la valeur devient un "lien" discret quand un détail existe. */
.cell-detail {
  cursor: pointer;
  border-radius: 3px;
}
.cell-detail:hover {
  background-color: rgba(43, 72, 162, 0.12);
  text-decoration: underline;
}
</style>

<style>
/* Hors `scoped` : popper-class d'un el-popover est téléporté hors du composant. */
.cell-detail-popover .cell-detail__title {
  font-weight: 700;
  font-size: 13px;
  color: #1c1e21;
  margin-bottom: 8px;
}
.cell-detail-popover .cell-detail__cellval {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cell-detail-popover .cell-detail__line {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  padding: 3px 0;
}
.cell-detail-popover .cell-detail__line--total {
  border-top: 1px solid #e5e7eb;
  margin-top: 2px;
  padding-top: 6px;
  font-weight: 700;
}
</style>
